// Istanbul, "Bosphorus by night": the historic peninsula (Blue Mosque, Hagia Sophia) seen across the
// Bosphorus from a rooftop tea garden, with Galata Tower, the suspension bridge, ferries and gulls,
// hanging mosaic lamps, tulip tea glasses, a sleeping tabby, a couple with their tea on the terrace wall and tulip
// petals on the wind.
// A floodlit rooftop sign on the ridge left of the Blue Mosque carries a tea ad (or the ad-space pitch).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, hash, rng, bayer, hex,
  makeCanvas, px, rect, makeHalo, drawHalo, streak, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const WL = 190;                                      // Bosphorus waterline
const LEDGE = 236;                                   // top of the terrace parapet
const FOCUS = 189;                                   // portrait crop: the whole Blue Mosque, the crescent, Hagia Sophia's first minaret
const GLOW_X = 232;                                  // centre of the mosque group (sky glow)
const MOON = { x: 236, y: 32 };
const VENUS = { x: 249, y: 27 };
const BM = { cx: 180, g: 152 };                      // Blue Mosque: centre x, ground
const HS = { cx: 285, g: 152 };                      // Hagia Sophia
const GT = { cx: 372, base: 150 };                   // Galata Tower
const BR = { tx: 440, top: 114, deck: 160, anc: 401, mid: 600, low: 156 };   // suspension bridge
// Maiden's Tower: 200 m off this shore, the nearest landmark, standing in the near water in front of the far quays.
// Sprite origin, size, tower centre column and the waterline row (in sprite rows); the far ferry passes behind, the near one in front.
const MT = { x: 96, y: 167, w: 47, h: 57, c: 23, wl: 54 };
const BB = { x: 74, y: 127, w: 48, h: 20 };          // rooftop billboard face (exported as `billboard`)

/* ================= Colour + pixel helpers ================= */
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = hex(a), B = hex(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
/** ordered-dither pick from a palette (v in 0..1); soft < 1 narrows the dithered band between tones */
function dpick(pal, v, i, y, soft = 1) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, y) - 0.5) * soft) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours). `own` tracks which building owns a pixel. */
function painter(w = W, h = H, track = false) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const own = track ? new Int32Array(w * h).fill(-1) : null;
  const p = { c, x, d, w, h, own, id: -1 };
  p.set = (i, y, col) => {
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
    if (own) own[y * w + i] = p.id;
  };
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function flipCanvas(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}

/* ================= Sky: deep blue, lighter at the horizon, a glow over the old city ================= */
const SKY = ['#02041a', '#040822', '#060c2b', '#091134', '#0c173e', '#101e48', '#142552', '#192d5c', '#1f3666',
  '#263f70', '#2e4978', '#38537f', '#445d85', '#52688a'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 186), 1.4);
    v += Math.max(0, 1 - Math.hypot((i - GLOW_X) / 250, (y - 168) / 105)) * 0.14;   // light dome over the old city
    v += Math.max(0, 1 - Math.hypot((i - 420) / 140, (y - 175) / 60)) * 0.06;      // Beyoglu glow
    const f = Math.min(1, v) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const STARS = [];
{
  const r = rng(12);
  while (STARS.length < 40) {
    const x = Math.floor(r() * W), y = 8 + Math.floor(r() * 100);
    if (Math.hypot(x - MOON.x - 6, y - MOON.y) < 26) continue;
    STARS.push({ x, y, k: 17 + Math.floor(r() * 70), ph: r(), b: 0.3 + r() * 0.6, big: r() < 0.1 });
  }
}
/** thin crescent, lit on the lower left, horns up; the evening star sits in its opening */
function renderMoon() {
  const R = 9, s = 2 * R + 3, c0 = R + 1; const [c, x] = makeCanvas(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c0, dy = y - c0, d1 = Math.hypot(dx, dy), d2 = Math.hypot(dx - 3.4, dy + 1.6);
    if (d1 > R + 0.25 || d2 < R + 0.3) continue;
    let col = '#fff6d6';
    if (d2 < R + 1.2) col = '#eadcaa';                     // terminator edge
    else if (d1 > R - 0.8) col = dx + dy > 2 ? '#e4d5a2' : '#fffbea';
    px(x, i, y, col);
  }
  return c;
}

/* ================= Faint clouds (tileable streaky bands, moonlit tops, city-lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 7, y: 10, h: 46, banks: 3, k: 1, pal: { top: '#22346a', body: '#111e48', core: '#0f1a42', bot: '#18264f' } },
  { seed: 19, y: 52, h: 44, banks: 3, k: 2, pal: { top: '#324a80', body: '#182a58', core: '#162652', bot: '#2c3a6c' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 100, base = h * (0.4 + r() * 0.4);
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = 9 + r() * 18, ry = 2.2 + r() * 3.4;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.6, rx, ry, base: base + ry * 0.2 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.6;
      if (v > best) best = v;
    }
    best += 0.2 * Math.sin(TAU * 7 * x / W + y * 0.9 + def.seed) * Math.sin(TAU * 3 * x / W - y * 0.3);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (v > 0.5 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}

/* ================= Palettes (light → dark) ================= */
const BMP = ['#fff5da', '#f7dda2', '#e7bd76', '#c69552', '#8f663a', '#5a3d29'];    // Blue Mosque stone, floodlit
const BMD = ['#fbf3e4', '#dddad4', '#b8b9bf', '#9095a4', '#686e84'];    // its domes: silvery lead, warm where the floodlights hit
const HSP = ['#ffe3be', '#f9ba86', '#e3935e', '#bd6e44', '#854834', '#4f2b25'];    // Hagia Sophia rose plaster
const HSD = ['#fcf0dc', '#e0d8cc', '#bcb6b2', '#948e96', '#6a6878'];    // its lead domes, a touch warmer
const MINP = ['#fff4de', '#f4dcb4', '#dcbc8c', '#b08a60', '#7a5a3e', '#4c3628'];   // white minarets
const BRICK = ['#f8b894', '#dc845c', '#b4623e', '#80422c', '#50281e', '#361a16'];  // the red-brick minaret
const CONE = ['#dcdee6', '#a4aabb', '#767e96', '#4e5572'];                         // lead minaret caps
const GTP = ['#ffe6b4', '#f4c47e', '#dca05a', '#b47a40', '#7e532e', '#4e3222'];    // Galata's gallery band, warm in its own light
const GTS = ['#ecd6a8', '#d2b584', '#b09165', '#876b4e', '#5d493f', '#3a2f34'];    // Galata's shaft: rubble stone, gold in its floodlights
const GTC = ['#d4cac6', '#a8a0a8', '#7a7488', '#524e66', '#323046'];               // Galata cone, lit from below
const SMP = ['#d8b27c', '#b48a58', '#8c6644', '#604432', '#402e28', '#2a1e20'];    // distant small mosque (dim)
const SMD = ['#d6c6a8', '#ab9c86', '#80725f', '#584e46', '#3a3432'];
const GOLD = '#ffd760', GOLD_D = '#c8922e', WIN_D = '#4a2e22';

/* ================= Architecture helpers: symmetric by construction =================
   Every symmetric element has an integer centre column and an odd width. Shapes are drawn as a left
   half and mirrored onto the right half; lighting only picks colours and never moves geometry. */
const L3 = (() => { const v = [-0.14, 0.42, 0.9], n = Math.hypot(...v); return v.map(a => a / n); })();   // floodlights below, slightly left
/** set pixel (i, y) and its mirror about column c; `col` may be a function of x (colour-only shading) */
function mset(p, c, i, y, col) {
  const j = 2 * c - i;
  p.set(i, y, typeof col === 'function' ? col(i) : col);
  if (j !== i) p.set(j, y, typeof col === 'function' ? col(j) : col);
}
/** symmetric run of columns c-hw..c+hw on row y */
function run(p, c, y, hw, col) { for (let i = c - hw; i <= c; i++) mset(p, c, i, y, col); }
/** banded floodlit dome: centre column cx, odd width 2*hw+1, h rows sitting on row `by` (exclusive).
    `ribs` lists column offsets from cx drawn one band darker below the crown (a ribbed dome). */
function dome(p, cx, hw, by, h, pal, amb = 0.4, ribs = null) {
  const R = hw + 0.5, n = pal.length;
  for (let j = 0; j < h; j++) {
    const y = by - 1 - j, dy = (j + 0.5) / h;
    const rw = Math.min(hw, Math.floor(R * Math.sqrt(Math.max(0, 1 - dy * dy))));
    for (let i = cx - rw; i <= cx; i++) mset(p, cx, i, y, x => {
      const nx = (x - cx) / R, nz = Math.sqrt(Math.max(0, 1 - nx * nx - dy * dy));
      const lum = amb + (1 - amb) * Math.max(0, nx * L3[0] - dy * L3[1] + nz * L3[2]);
      const k = Math.floor((1 - lum) / (1 - amb) * n) + (ribs && j < h - 2 && ribs.includes(Math.abs(x - cx)) ? 1 : 0);
      return pal[Math.min(n - 1, k)];
    });
  }
}
/** gold finial (alem) on column cx: a rod of `h` rows standing on row `y`, crowned by a crescent, horns up */
function alem(p, cx, y, h) {
  for (let k = 0; k < h; k++) p.set(cx, y - k, k % 2 ? GOLD : GOLD_D);
  p.set(cx, y - h, GOLD_D); p.set(cx - 1, y - h - 1, GOLD); p.set(cx + 1, y - h - 1, GOLD);
}
/** symmetric floodlit wall block: columns cx-hw..cx+hw, rows y0..y1 (lower rows brighter, corners one band darker) */
function block(p, cx, hw, y0, y1, pal, cornice = true) {
  for (let y = y0; y <= y1; y++) {
    const bi = (y - y0) < (y1 - y0) * 0.4 ? 2 : 1;
    run(p, cx, y, hw, x => cornice && y === y0 ? pal[0] : cornice && y === y0 + 1 ? pal[3] : pal[Math.abs(x - cx) === hw ? bi + 1 : bi]);
  }
}
/** symmetric row of windows at offsets start, start+step, … ≤ maxOff (0 = one on the centre column) */
function windows(p, cx, start, step, maxOff, y0, hgt, arch, dark) {
  for (let o = start; o <= maxOff; o += step) for (let y = y0; y < y0 + hgt; y++) {
    const col = y === y0 && arch ? arch : dark;
    p.set(cx - o, y, col); p.set(cx + o, y, col);
  }
}
/** pencil minaret on centre column cx: 3-px shaft, 5-px balconies (topmost first), cap narrowing 5→3→1, gold finial at cx.
    `cap` = rows from the tip to the cap's eave, `ped` = top row of a 5-px pedestal, `top` = palette of the shaft and
    balcony from the topmost balcony up (the brick minaret's stone head) */
function minaret(p, cx, base, tip, balc, pal, cone, lights, { cap = 11, ped = base + 1, top = pal } = {}) {
  const eave = tip + cap, k3 = Math.round(cap * 0.3), spire = eave - k3 - tip - 3;
  for (let y = eave + 1; y <= base; y++) {
    const P = y <= balc[0] ? top : pal;
    if (y > ped) run(p, cx, y, 2, x => x === cx + 2 ? P[3] : x === cx + 1 ? P[2] : P[1]);
    else if (y === ped) run(p, cx, y, 2, P[0]);
    else { p.set(cx - 1, y, P[1]); p.set(cx, y, P[1]); p.set(cx + 1, y, P[2]); }
  }
  run(p, cx, eave, 2, x => Math.abs(x - cx) === 2 ? cone[2] : cone[1]);
  for (let y = eave - k3; y < eave; y++) { p.set(cx - 1, y, cone[1]); p.set(cx, y, cone[0]); p.set(cx + 1, y, cone[2]); }
  for (let y = tip + 3; y < eave - k3; y++) p.set(cx, y, y < tip + 3 + Math.ceil(spire * 0.6) ? cone[2] : cone[1]);
  p.set(cx, tip, GOLD); p.set(cx, tip + 1, GOLD_D); p.set(cx, tip + 2, GOLD);
  balc.forEach((by, i) => {
    const P = i === 0 ? top : pal;
    run(p, cx, by, 2, x => Math.abs(x - cx) === 2 ? P[1] : P[0]);        // lit balcony ledge
    run(p, cx, by + 1, 2, P[4]);                                            // its shadow
    run(p, cx, by + 2, 1, P[3]);                                            // corbel
    p.set(cx - 2, by - 1, P[2]); p.set(cx + 2, by - 1, P[2]);              // railing posts (lamps twinkle here)
    if (lights) lights.push({ x: cx - 2, y: by - 1, id: lights.length });
  });
}
/** weight tower / turret on centre column cx with a little dome and a finial */
function turret(p, cx, top, bot, pal, dpal) {
  for (let y = top; y < bot; y++) { p.set(cx - 1, y, pal[1]); p.set(cx, y, pal[1]); p.set(cx + 1, y, pal[2]); }
  dome(p, cx, 2, top, 3, dpal);
  p.set(cx, top - 5, GOLD); p.set(cx, top - 4, GOLD_D);
}
/** balcony rows (topmost first) spaced down the upper shaft like the real ones: three, two or one */
const BALC_AT = { 3: [0.37, 0.52, 0.67], 2: [0.4, 0.58], 1: [0.42] };
const balconies = (tip, base, n) => BALC_AT[n].map(f => tip + Math.round((base - tip) * f));
const LONG_CAP = 16;                                                // the tall lead "pencil" caps of the imperial mosques

/* ================= The Blue Mosque: a cascade of lead domes, six minarets, the domed courtyard portico =================
   All six minarets are the same 64 m, half as tall again as the 43 m main dome. Seen from the water the four prayer-hall
   minarets (three balconies each) lead, level with each other; the two at the courtyard's outer corners (two balconies
   each) stand farther off and read a little lower. */
function renderBlueMosque(p, bl) {
  const c = BM.cx, g = BM.g;
  const pair = (o, tip, n) => { for (const s of [-1, 1]) minaret(p, c + s * o, g, tip, balconies(tip, g, n), BMP, CONE, bl, { cap: LONG_CAP }); };
  pair(32, 77, 3);                                                  // prayer-hall corners on the dome side
  pair(40, 76, 3);                                                  // prayer-hall corners where the hall meets the courtyard
  block(p, c, 29, 130, g, BMP);                                     // prayer-hall walls (the courtyard hides their foot)
  windows(p, c, 1, 4, 27, 132, 3, BMP[3], WIN_D);
  block(p, c, 22, 124, 129, BMP, false);                            // walls under the semi-domes
  windows(p, c, 2, 4, 18, 125, 3, BMP[3], BMP[4]);
  for (const s of [-1, 1]) turret(p, c + s * 15, 111, 124, BMP, BMD);   // the weight towers (the near two), rising behind
  for (const s of [-1, 1]) dome(p, c + s * 13, 10, 124, 7, BMD);    // side semi-domes falling away outwards
  for (const s of [-1, 1]) dome(p, c + s * 25, 3, 130, 4, BMD);     // corner domes
  block(p, c, 12, 112, 115, BMP, false);                            // drum with its ring of windows, dimly lit
  windows(p, c, 1, 2, 11, 113, 2, '#d89a52', '#9a6036');
  dome(p, c, 12, 112, 10, BMD, 0.46);                               // main dome
  alem(p, c, 101, 4);
  dome(p, c, 11, 126, 10, BMD);                                     // front semi-dome
  for (const s of [-1, 1]) dome(p, c + s * 8, 3, 130, 4, BMD);      // exedrae at its feet
  // the courtyard portico in front: arcade wall, a small lead dome over every bay, the gate in the middle
  block(p, c, 49, 141, g, BMP);
  for (let o = 9; o <= 44; o += 7) for (const s of [-1, 1]) {
    dome(p, c + s * o, 2, 141, 3, BMD);
    for (let y = 143; y <= 147; y++) run(p, c + s * o, y, y === 143 ? 0 : 1, WIN_D);   // the bay's arch
  }
  dome(p, c, 3, 135, 3, BMD);                                       // the gate: a tall portal under a little dome
  block(p, c, 5, 135, g, BMP);
  for (let y = 138; y <= g; y++) run(p, c, y, y === 138 ? 0 : y === 139 ? 1 : 2, y === 138 ? BMP[3] : WIN_D);
  pair(50, 82, 2);                                                  // the courtyard's outer corners, farther off: a little lower
}

/* ================= Hagia Sophia: broad ribbed dome, buttress towers, four minarets that don't match ================= */
const HS_RIBS = Array.from({ length: 9 }, (_, k) => Math.round(17.5 * Math.sin((k * 9 + 4.5) * Math.PI / 180)));   // 40 ribs, half seen
function renderHagiaSophia(p, bl) {
  const c = HS.cx, g = HS.g;
  for (const s of [-1, 1]) minaret(p, c + s * 38, g, 85, balconies(85, g, 2), MINP, CONE, bl, { cap: LONG_CAP, ped: 130 });   // Sinan's sturdy western twins
  block(p, c, 31, 134, g, HSP);                                     // massive lower body
  windows(p, c, 0, 4, 28, 138, 3, HSP[4], WIN_D);
  windows(p, c, 2, 4, 26, 144, 1, null, WIN_D);
  for (const s of [-1, 1]) dome(p, c + s * 26, 5, 134, 5, HSD);     // flank exedrae
  block(p, c, 20, 126, 133, HSP, false);                            // side aisles
  for (let k = 21; k <= 27; k++) {                                  // buttress towers, one shape mirrored
    const top = k >= 25 ? 120 : 117;
    for (let y = top; y <= g; y++) {
      const col = y === top ? HSP[0] : y === top + 1 ? HSP[3] : (k === 21 || k === 27 || k === 24) ? HSP[2] : HSP[1];
      p.set(c - k, y, col); p.set(c + k, y, col);
    }
  }
  for (let y = 119; y < 134; y++) { p.set(c - 20, y, HSP[4]); p.set(c + 20, y, HSP[4]); }
  block(p, c, 17, 119, 126, HSP, false);                            // square block under the drum
  windows(p, c, 0, 3, 15, 123, 4, HSP[4], WIN_D);
  for (let y = 115; y <= 118; y++) run(p, c, y, 16, x => {          // drum ringed with lit windows
    const k = Math.abs(x - c);
    if (k % 2 === 0 && k <= 14 && (y === 116 || y === 117)) return y === 116 ? '#ffe2a0' : '#ffbe62';
    return k === 16 ? HSP[2] : HSP[1];
  });
  dome(p, c, 17, 115, 8, HSD, 0.48, HS_RIBS);                       // the great shallow dome, ribbed
  windows(p, c, 3, 4, 15, 114, 1, null, HSP[1]);                    // little buttresses at its base
  alem(p, c, 106, 4);
  dome(p, c, 13, 128, 8, HSD);                                      // eastern semi-dome
  dome(p, c, 7, 134, 5, HSD);                                       // apse
  minaret(p, c - 32, g, 92, balconies(92, g, 1), BRICK, CONE, bl, { cap: LONG_CAP, top: MINP });   // red brick, a stone head
  minaret(p, c + 32, g, 88, balconies(88, g, 1), MINP, CONE, bl, { cap: LONG_CAP });   // the slender white one
}

/* ================= Galata Tower: a fat stone cylinder, the lit arcade between a cornice and the balcony, a set-back
   drum and a small cone, narrower than the shaft (all centred on GT.cx) ================= */
function renderGalata(p) {
  const c = GT.cx, col = [4, 3, 2, 1, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 3, 3, 4, 4, 5];   // round, lit from the left
  const ring = (y, hw, pal, d = 0) => {                              // one row of a drum hw each side, d steps darker
    for (let k = -hw; k <= hw; k++) p.set(c + k, y, pal[Math.min(pal.length - 1, col[Math.round((k + hw) * 9 / hw)] + d)]);
  };
  // the shaft: rubble stone four times as tall as it is wide, plain but for a few small windows
  for (let y = 109; y <= GT.base + 8; y++) {
    ring(y, 9, GTS, y === 109 ? 1 : 0);                               // the cornice's shadow
    for (let k = -8; k <= 8; k++) if (y > 109 && hash(c + k, y, 9) < 0.08) p.set(c + k, y, GTS[Math.min(5, col[k + 9] + 1)]);
  }
  for (const [wx, wy, lit] of [[-5, 112, 1], [0, 112], [5, 112, 1], [-3, 119], [3, 119, 1], [0, 127], [-5, 135], [4, 141]]) {
    p.set(c + wx, wy, lit ? '#ffd88a' : WIN_D); p.set(c + wx, wy + 1, lit ? '#e0924a' : WIN_D);   // a few lit, like the real ones
  }
  // the arcade: tall round arches glowing from inside, standing on a cornice
  ring(107, 10, GTP); ring(108, 10, GTP, 2);
  for (let y = 97; y <= 106; y++) ring(y, 9, GTP, 1);
  for (const [k0, k1] of [[-8, -7], [-5, -3], [-1, 1], [3, 5], [7, 8]]) for (let y = 98; y <= 104; y++) for (let k = k0; k <= k1; k++) {
    if (y === 98 && k * 2 !== k0 + k1) continue;                      // round heads
    p.set(c + k, y, y <= 100 ? '#fff2c4' : y <= 102 ? '#ffc668' : '#e0924a');
  }
  // the balcony on its corbels, its railing, and the narrower drum whose doors open onto it
  for (let k = -9; k <= 9; k++) p.set(c + k, 96, Math.abs(k) % 2 ? GTP[4] : GTP[2]);
  ring(95, 10, GTP);
  for (let y = 92; y <= 94; y++) {
    ring(y, 7, GTP, 1);
    for (const k of [-6, -3, 0, 3, 6]) p.set(c + k, y, y === 92 ? '#ffd88a' : '#e0924a');
  }
  for (const k of [8, 9, 10]) { p.set(c - k, 94, GTP[3]); p.set(c + k, 94, GTP[4]); }
  // the lead roof: a skirt over the drum, a short neck and the cone
  ring(91, 7, GTC); ring(90, 4, GTP, 3);
  [0, 1, 1, 2, 2, 3, 3, 4, 4, 5].forEach((hw, n) => {
    for (let k = -hw; k <= hw; k++) p.set(c + k, 80 + n, GTC[Math.min(4, Math.floor((0.06 + 0.5 * (hw ? k / hw + 1 : 1) / 2 + 0.34 * (9 - n) / 9) * 5))]);
  });
  for (let y = 75; y <= 79; y++) p.set(c, y, y === 77 ? GOLD_D : GOLD);
}

/* ================= Far shores, the Golden Horn, the suspension bridge ================= */
function renderFar() {
  const p = painter(), r = rng(21), lights = [];
  for (let i = 296; i < W; i++) {
    const top = Math.round(173 + 2.5 * Math.sin(i * 0.05) + 1.5 * Math.sin(i * 0.17 + 1) - 5 * smooth(296, 340, i) * (1 - smooth(360, 420, i)));
    for (let y = top; y < WL; y++) p.set(i, y, y === top ? '#26395f' : '#18264a');
    if (r() < 0.3) lights.push({ id: lights.length, x: i, y: top + 2 + Math.floor(r() * Math.max(1, WL - top - 5)), n: 6 + Math.floor(r() * 18), ph: r(), col: r() < 0.8 ? '#ffc070' : '#d8e4ff' });
  }
  for (let i = 318; i < 352; i++) for (let y = 181; y < WL; y++) p.set(i, y, dpick(['#26406e', '#182a52', '#0f1c40'], (y - 181) / 8, i, y));
  for (let i = 318; i < 352; i++) { p.set(i, 182, '#3a4670'); p.set(i, 183, '#141a30'); }
  for (let i = 320; i < 352; i += 4) lights.push({ id: lights.length, x: i, y: 181, n: 2, ph: 0.3, col: '#ffd690', fixed: true });
  return { c: p.done(), lights };
}
const bMain = x => BR.low - (BR.low - BR.top) * ((x - BR.mid) / (BR.mid - BR.tx)) ** 2;
const bSide = x => { const s = (BR.tx - x) / (BR.tx - BR.anc); return lerp(BR.top, BR.deck + 3, s) + 4 * Math.sin(Math.PI * s); };
function renderBridge() {
  const p = painter(), leds = [];
  for (let i = 388; i < W; i++) { p.set(i, BR.deck, '#34426a'); p.set(i, BR.deck + 1, '#161e38'); }
  for (let i = 390; i < 404; i += 4) for (let y = BR.deck + 2; y < 180; y++) p.set(i, y, '#141c34');
  for (let i = BR.tx + 3; i < W; i += 3) for (let y = Math.round(bMain(i)) + 1; y < BR.deck; y++) p.set(i, y, '#1a2340');
  for (let i = BR.anc + 3; i < BR.tx - 3; i += 3) for (let y = Math.round(bSide(i)) + 1; y < BR.deck; y++) p.set(i, y, '#1a2340');
  for (let y = BR.top - 1; y < 182; y++) {
    p.set(BR.tx - 3, y, '#3c4e7a'); p.set(BR.tx - 2, y, '#26325a');
    p.set(BR.tx + 2, y, '#34466e'); p.set(BR.tx + 3, y, '#1e2848');
  }
  for (const y of [BR.top - 1, BR.top, 130, 146, BR.deck + 3]) for (let i = BR.tx - 1; i <= BR.tx + 1; i++) p.set(i, y, '#28345a');
  const line = (fn, x0, x1) => {
    let prev = Math.round(fn(x0));
    for (let i = x0; i <= x1; i++) {
      const y = Math.round(fn(i));
      for (let yy = Math.min(prev, y); yy <= Math.max(prev, y); yy++) p.set(i, yy, '#4c5e8a');
      prev = y;
    }
  };
  line(bMain, BR.tx, W - 1); line(bSide, BR.anc, BR.tx);
  for (let i = 392; i < W; i += 5) p.set(i, BR.deck - 1, '#b08a58');
  for (let i = BR.tx + 2; i < W; i += 2) leds.push({ x: i, y: Math.round(bMain(i)), s: (i - BR.tx) / 90 });
  for (let i = BR.anc + 1; i < BR.tx - 1; i += 2) leds.push({ x: i, y: Math.round(bSide(i)), s: (i - BR.tx) / 90 });
  for (let y = BR.top + 3; y < BR.deck - 1; y += 3) { leds.push({ x: BR.tx - 3, y, s: (BR.top - y) / 120 }); leds.push({ x: BR.tx + 3, y, s: (BR.top - y) / 120 }); }
  return { c: p.done(), leds };
}

/* ================= The city: hillsides of houses, both mosques, Galata, the shore ================= */
// one shared, flat ground line (y = 152) under both mosques, falling to the point of the peninsula at x ≈ 342
const penY = x => Math.round(184 - 32 * smooth(-30, 140, x) * (1 - smooth(328, 343, x)));
const galY = x => Math.round(186 - 34 * (x < GT.cx ? smooth(346, GT.cx, x) : 1 - 0.72 * smooth(GT.cx + 2, 448, x)));
const HROW = ['#232d50', '#1d2647', '#19213f', '#151c37', '#121830', '#0f152a', '#0d1226'];
const WINC = ['#ffd27a', '#ffc462', '#ffb050', '#f59a44', '#ffe2a0', '#cfe0ff'];
function renderSkyline() {
  const p = painter(W, H, true), r = rng(5), wins = [], bl = [], lamps = [];
  let bid = 0;
  const building = (bx, bw, top, bot, col0, winP, tall) => {
    p.id = bid++;
    const tint = r(), col = tint < 0.08 ? mixHex(col0, '#4a3444', 0.45) : mixHex(col0, tint < 0.55 ? '#2e2648' : '#1c3450', r() * 0.3);
    const roof = mixHex(col, '#6a78a8', 0.14 + r() * 0.12);
    for (let y = top; y <= bot; y++) for (let i = bx; i < bx + bw; i++) p.set(i, y, col);
    for (let i = bx; i < bx + bw; i++) p.set(i, top, roof);
    const kind = r();
    if (kind < 0.35 && bw >= 5) {
      for (let k = 1; k <= Math.min(2, (bw >> 1) - 1); k++) for (let i = bx + k; i < bx + bw - k; i++) p.set(i, top - k, k === 2 ? roof : col);
    } else if (kind < 0.5) {
      const cx = bx + 1 + Math.floor(r() * (bw - 2));
      p.set(cx, top - 1, roof); p.set(cx, top - 2, col);
    }
    for (let wy = top + 2; wy <= Math.min(bot - 1, top + (tall ? 24 : 12)); wy += tall ? 2 : 3)
      for (let wx = bx + 1; wx < bx + bw - 1; wx += 2)
        if (r() < winP) wins.push({ id: wins.length, x: wx, y: wy, b: p.id, n: 2 + Math.floor(r() * 9), p: 0.35 + r() * 0.5, off: r(),
          col: WINC[Math.floor(r() * r() * WINC.length)] });
  };
  const tree = (cx, cy, rx, ry, g) => {
    p.id = -3;
    for (let y = Math.floor(cy - ry - 1); y <= Math.ceil(cy + ry); y++) for (let i = Math.floor(cx - rx - 1); i <= Math.ceil(cx + rx + 1); i++) {
      const dx = (i - cx) / rx, dy = (y - cy) / ry;
      const v = 1 - dx * dx - dy * dy + (bayer(i, y) - 0.5) * 0.45 + (g() - 0.5) * 0.25;
      if (v <= 0) continue;
      let col = v > 0.5 ? '#0e1c26' : '#12242c';
      if (dy < -0.15 && v < 0.6) col = dy < -0.55 ? '#46583a' : '#2c4432';
      p.set(i, y, col);
    }
  };
  const cypress = (cx, base, hgt) => {
    p.id = -3;
    for (let y = base - hgt; y <= base; y++) {
      const f = (y - (base - hgt)) / hgt, hw = f < 0.15 ? 0 : f < 0.4 ? 1 : f < 0.85 ? 1.5 : 1;
      for (let i = Math.round(cx - hw); i <= Math.round(cx + hw); i++) p.set(i, y, i < cx ? '#2a4434' : (i + y) % 3 === 0 ? '#16281f' : '#1c3226');
    }
  };
  // the hill itself, so no sky shows below the ridge between the houses
  p.id = -5;
  const ground = HROW.slice(3);
  for (let i = 0; i < 342; i++) for (let y = penY(i) + 1; y < 186; y++) p.set(i, y, dpick(ground, (y - penY(i)) / 24, i, y));
  // houses beyond the ridge, seen between the monuments
  for (let bx = 124; bx < 342;) {
    const bw = 4 + Math.floor(r() * 8);
    if (r() < 0.7) building(bx, bw, penY(bx + bw / 2) - 1 - Math.floor(r() * r() * 11), 186, HROW[0], 0.3);
    bx += bw + (r() < 0.3 ? 2 : 0);
  }
  for (let bx = 350; bx < W;) { const bw = 5 + Math.floor(r() * 7); building(bx, bw, galY(bx + bw / 2) - 4 - Math.floor(r() * 6), 187, HROW[0], 0.35, true); bx += bw; }
  // a small domed mosque down the slope on the left
  p.id = -2;
  { const sx = 44, sg = penY(sx);
    block(p, sx, 8, sg - 9, sg, SMP);
    dome(p, sx, 7, sg - 9, 7, SMD);
    for (let y = sg - 20; y < sg - 16; y++) p.set(sx, y, GOLD_D);
    minaret(p, sx + 11, sg, sg - 36, [sg - 26], SMP, ['#cdbfa8', '#a09082', '#766a68', '#4e4854'], null); }
  renderBlueMosque(p, bl);
  renderHagiaSophia(p, bl);
  renderGalata(p);
  // cypresses round the mosques
  for (const [cx, hgt] of [[224, 15], [229, 19], [235, 14], [240, 17], [131, 13], [330, 12], [58, 11]]) cypress(cx, penY(cx) + 3, hgt);
  // the slope down to the shore: Sultanahmet's houses climbing to the Blue Mosque, the square's park between the two
  // mosques, the gardens falling from Hagia Sophia to the point. Each row of trees stands among a row of houses, whose
  // nearer row hides its feet; the topmost hide the mosques' foot.
  const rt = rng(57), wood = x => x < 222 ? 0.5 : x < 250 ? 1 : 0.85;
  const grove = row => {
    for (let x = 128 + Math.floor(rt() * 5); x < 330; x += 4 + Math.floor(rt() * 6)) {
      const gy = penY(x) + 5 + row * 5, rx = 3 + rt() * 3, ry = 2.5 + rt() * 2;
      if (rt() < wood(x) * (row < 0 ? 0.75 : 0.5 - row * 0.08) && gy < 182) tree(x, gy - ry + 2, rx, ry, rt);
    }
  };
  grove(-1);
  // hillside rows stepping down to the shore; under the mosques the first row's roofs stop at their foot
  for (let row = 0; row < 7; row++) {
    let bx = -Math.floor(r() * 8);
    while (bx < 342) {
      const bw = 5 + Math.floor(r() * 9), gy = penY(bx + bw / 2) + 5 + row * 5;
      if (gy < 186) {
        let top = gy - (3 + Math.floor(r() * 6));
        if (row === 0 && bx > 136 && bx < 330) top = penY(bx) + 1 + Math.floor(rt() * 5);
        building(bx, Math.min(bw, 342 - bx), top, 186, HROW[Math.min(6, row + 1)], 0.5);
      }
      bx += bw;
    }
    if (row < 4) grove(row);
  }
  // the floodlights at the mosques' foot catch the roofs and treetops just below them
  for (const [c, hw, col] of [[BM.cx, 50, '#ffc47e'], [HS.cx, 42, '#ffa672']]) {
    const k = rgbOf(col);
    for (let i = c - hw; i <= c + hw; i++) for (let y = penY(i) - 4; y <= penY(i) + 5; y++) {
      if (p.own[y * W + i] !== -3 && p.own[y * W + i] < 0) continue;              // trees and houses only
      const v = (1 - Math.abs(y - penY(i) + 1) / 6) * (1 - smooth(hw - 10, hw, Math.abs(i - c)));
      const f = 0.36 * Math.floor(v * 3 + bayer(i, y)) / 3, o = (y * W + i) * 4;
      if (f > 0) for (let ch = 0; ch < 3; ch++) p.d[o + ch] = Math.round(lerp(p.d[o + ch], k[ch], f));
    }
  }
  for (let row = 0; row < 8; row++) {
    let bx = 348 - Math.floor(r() * 2);
    while (bx < W) {
      const bw = 5 + Math.floor(r() * 8), gy = galY(bx + bw / 2) + 3 + row * 5;
      if (gy < 187) building(bx, bw, gy - (4 + Math.floor(r() * 8)), 187, HROW[Math.min(6, row + 1)], 0.55, true);
      bx += bw;
    }
  }
  // sea walls, coastal road and quays
  p.id = -4;
  for (let i = 0; i <= 341; i++) {
    for (let y = 186; y < WL; y++) p.set(i, y, y === 186 ? '#3a3458' : ((i + (y & 1) * 3) % 7 === 0 ? '#1a1628' : '#241e36'));
    if ((i & 3) < 2) p.set(i, 185, '#2c2644');
  }
  for (let lx = 6; lx < 340; lx += 14) { p.set(lx, 184, '#1a1426'); p.set(lx, 183, '#ffd48a'); lamps.push({ x: lx, y: 183 }); }
  for (let i = 347; i < W; i++) for (let y = 187; y < WL; y++) p.set(i, y, y === 187 ? '#3a3458' : '#221c34');
  for (let i = 386; i < 406; i++) for (let y = 181; y < 187; y++) p.set(i, y, y === 181 ? '#4a4468' : y === 183 ? '#ffcf80' : '#1c1830');
  for (let lx = 354; lx < W; lx += 16) { p.set(lx, 186, '#ffd48a'); lamps.push({ x: lx, y: 186 }); }
  // keep only windows whose pixel still belongs to their own building
  const vis = wins.filter(w => p.own[w.y * W + w.x] === w.b);
  vis.forEach((w, i) => { w.id = i; });
  return { c: p.done(), wins: vis, bl, lamps };
}

/* ================= Maiden's Tower on its islet =================
   A quay wall round the rock, the long low building, the square tower rising from it with a lit lantern under a lead
   cupola and the flag on top, and the little lighthouse turret on its left. Floodlit from the front left. */
function renderMaiden() {
  const { w, h, c } = MT, B = 34, p = painter(w, h);                    // B: the low building's roof line
  const S = ['#fff3dc', '#efd6ae', '#cfaa80', '#98755a', '#5e4636'], LD = ['#ece4e2', '#c0b6bc', '#8e8696', '#625c70'];
  const LIT = '#ffd27a', LIT2 = '#ffe9b0';
  const wall = (x0, x1, y0, y1) => { for (let y = y0; y <= y1; y++) for (let i = x0; i <= x1; i++) p.set(i, y, dpick(S.slice(0, 4), 0.08 + 0.84 * (i - x0) / (x1 - x0), i, y, 0.5)); };
  // the rock, awash at the waterline (row MT.wl)
  for (let i = 0; i < w; i++) {
    const rt = B + 17 + Math.round(0.9 * Math.sin(i * 0.8) + 0.7 * Math.sin(i * 0.31 + 1) + (i < 3 || i > w - 4 ? 1.5 : 0));
    for (let y = rt; y < h; y++) p.set(i, y, y === rt ? (i < c + 8 ? '#5a4c5a' : '#4a4050') : (y === rt + 1 && (i * 5) % 7 === 0 ? '#3a3244' : '#2a2434'));
  }
  // the quay wall round the islet, with bollards along its lit coping
  for (let i = 1; i < w - 1; i++) for (let y = B + 12; y <= B + 16; y++) {
    const k = y - B - 12;
    let col = k === 0 ? (i < 32 ? S[0] : S[1]) : k === 1 ? S[3] : k === 4 ? S[4] : dpick([S[1], S[2], S[3]], 0.15 + 0.75 * i / w, i, y, 0.6);
    if ((k === 2 || k === 3) && (i + (k === 3 ? 3 : 0)) % 6 === 0) col = S[3];   // ashlar joints
    p.set(i, y, col);
  }
  for (let i = 3; i < w - 2; i += 6) p.set(i, B + 11, i < 32 ? S[1] : S[2]);
  // the long low building: cornice, a row of lit arched windows, the door
  for (let i = 5; i <= 41; i++) { p.set(i, B, i < c + 8 ? S[0] : S[1]); p.set(i, B + 1, S[3]); }
  wall(6, 40, B + 2, B + 11);
  for (let i = 8; i <= 38; i += 3) if (Math.abs(i - c) > 1) for (let y = B + 4; y <= B + 7; y++) p.set(i, y, y === B + 4 ? LIT2 : LIT);
  for (let y = B + 7; y <= B + 11; y++) for (let i = c - 1; i <= c + 1; i++)
    p.set(i, y, y === B + 7 ? (i === c ? WIN_D : S[3]) : y < B + 10 ? '#ffc060' : '#e89a48');
  // the square tower: ledge and railing on top, two storeys of windows, a lit left edge
  wall(c - 6, c + 6, 18, B - 1);
  for (let y = 18; y < B; y++) { p.set(c - 6, y, S[0]); p.set(c + 6, y, S[4]); }
  for (const [o, y0, lit] of [[-3, 20, 1], [0, 20, 1], [3, 20, 1], [-3, 26, 1], [0, 26, 0], [3, 26, 1]])
    for (let y = y0; y <= y0 + 2; y++) p.set(c + o, y, lit ? (y === y0 ? LIT2 : LIT) : WIN_D);
  for (let i = c - 8; i <= c + 8; i++) { p.set(i, 16, i < c + 3 ? S[0] : S[1]); p.set(i, 17, S[3]); }
  for (let i = c - 7; i <= c + 7; i += 2) p.set(i, 15, S[2]);
  // the lantern (the lighthouse): a ring of lit windows under a cornice, the lead cupola, the flag
  for (let y = 11; y <= 14; y++) for (let k = -4; k <= 4; k++)
    p.set(c + k, y, Math.abs(k) % 2 ? (y === 11 ? LIT2 : y === 14 ? LIT : '#fff4d0') : S[k <= 0 ? 1 : k === 2 ? 2 : 3]);
  for (let i = c - 5; i <= c + 5; i++) p.set(i, 10, i < c + 2 ? S[0] : S[2]);
  dome(p, c, 4, 10, 4, LD);
  p.set(c, 5, GOLD_D); for (let y = 1; y <= 4; y++) p.set(c, y, y & 1 ? '#d8d0c8' : '#a8a0a0'); p.set(c, 0, GOLD);
  for (const [dx, dy, col] of [[1, 1, '#e8303c'], [2, 1, '#f4ecea'], [3, 1, '#e8303c'], [1, 2, '#b81e2e'], [2, 2, '#c82432'], [3, 2, '#b81e2e']]) p.set(c + dx, dy, col);
  // the little turret on the left, on the low roof: shaft, lit lamp room, lead cap
  wall(6, 10, B - 9, B - 1);
  for (let i = 5; i <= 11; i++) p.set(i, B - 10, i < 9 ? S[0] : S[1]);
  for (let y = B - 13; y <= B - 11; y++) for (let i = 6; i <= 10; i++) p.set(i, y, i === 6 || i === 10 ? S[2] : y === B - 13 ? LIT2 : LIT);
  for (let i = 6; i <= 10; i++) p.set(i, B - 14, LD[i < 8 ? 0 : i === 8 ? 1 : 2]);
  for (let i = 7; i <= 9; i++) p.set(i, B - 15, LD[i < 8 ? 1 : 2]);
  p.set(8, B - 16, GOLD_D); p.set(8, B - 17, GOLD);
  return p.done();
}

/* ================= City ferries: white, yellow funnel with a black top, lit windows ================= */
function makeFerry(big) {
  const s = big ? 1 : 0.72, w = big ? 64 : 46, h = big ? 22 : 16, X = v => Math.round(v * s);
  const R = big ? { fun: [1, 8, 2], wheel: [6, 9], upper: [7, 9], main: [10, 14], hull: [15, 21] }
    : { fun: [1, 5, 1], wheel: [4, 6], upper: [5, 6], main: [7, 9], hull: [10, 15] };
  const p = painter(w, h);
  const WH = '#f6f3ea', WH2 = '#dcd8d0', WH3 = '#aeaab0', NAVY = '#1c2440', NAVY2 = '#111729', LW = '#ffd98a', LW2 = '#f0b058';
  const [h0, h1] = R.hull;
  for (let y = h0; y <= h1; y++) {                                  // hull, raked bow on the right
    const x0 = X(2) + Math.max(0, y - (h1 - 3)), x1 = w - 1 - Math.round((y - h0) * (big ? 1 : 0.8));
    for (let i = x0; i <= x1; i++) {
      let col = y >= h1 - 1 ? (y === h1 ? NAVY2 : NAVY) : y === h0 ? WH2 : WH;
      if (y === h0 + 2 && big && (i % 5) === 2 && i > 6 && i < x1 - 3) col = LW;
      if (y === h0 + 2 && !big && (i % 4) === 1 && i > 4 && i < x1 - 3) col = LW;
      if (i >= x1 - 1 && y < h1 - 1) col = WH2;
      p.set(i, y, col);
    }
  }
  const [m0, m1] = R.main;                                          // main deck saloon
  for (let y = m0; y <= m1; y++) for (let i = X(7); i <= X(55); i++) {
    let col = y === m1 ? WH3 : WH;
    const winRow = big ? (y === m0 + 1 || y === m0 + 2) : y === m0 + 1;
    if (winRow && i > X(7) + 1 && i < X(55) - 1) col = (i - X(7)) % 3 === 0 ? WH2 : (y === m0 + 1 ? LW : LW2);
    if (i === X(55)) col = WH2;
    p.set(i, y, col);
  }
  const [u0, u1] = R.upper;                                         // upper deck cabin
  for (let y = u0; y <= u1; y++) for (let i = X(16); i <= X(41); i++) {
    let col = y === u0 ? WH2 : WH;
    if (y === u0 + 1 && (i - X(16)) % 2 === 1 && i < X(41)) col = LW;
    p.set(i, y, col);
  }
  for (let i = X(14); i <= X(52); i += 2) p.set(i, u0 - 1, WH3);   // deck railing
  const [w0, w1] = R.wheel;                                         // wheelhouse
  for (let y = w0; y <= w1; y++) for (let i = X(42); i <= X(50); i++) p.set(i, y, y === w0 ? WH2 : (y === w0 + 1 && i > X(42) && i < X(50)) ? '#28324e' : WH);
  const [f0, f1, fb] = R.fun;                                       // funnel, slightly raked
  for (let y = f0; y <= f1; y++) {
    const fx = X(26) - (y < f0 + 3 ? 1 : 0);
    for (let k = 0; k < (big ? 5 : 4); k++) p.set(fx + k, y, y < f0 + fb ? '#16161e' : k < (big ? 3 : 2) ? '#f7c630' : '#c8921e');
  }
  for (let y = 1; y <= w0; y++) p.set(X(46), y, '#3a3c4c');        // mast + masthead light
  p.set(X(46), 0, '#ffffff');
  for (let y = m0 - 3; y < m0; y++) p.set(X(3), y, '#3a3c4c');     // stern flagstaff with a small red flag
  p.set(X(3) - 1, m0 - 3, '#e0282c'); p.set(X(3) - 2, m0 - 3, '#e0282c'); p.set(X(3) - 1, m0 - 2, '#e0282c'); p.set(X(3) - 2, m0 - 2, '#f4f0f0');
  p.set(w - 3, h0 - 1, '#3a3c4c'); p.set(w - 3, h0 - 2, '#ff4040');   // bow light
  return p.done();
}

/* ================= Water ================= */
function renderWater() {
  const hh = LEDGE - WL + 2; const [c, x] = makeCanvas(W, hh);
  const P = ['#0f1d44', '#0c1838', '#09122e', '#070e26'].map(hex);
  const img = x.createImageData(W, hh), d = img.data;
  for (let y = 0; y < hh; y++) for (let i = 0; i < W; i++) {
    const f = (y / hh) * (P.length - 1) * 0.999;
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = P[Math.min(k, P.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Terrace foreground: parapet, tulips, kilims, tea tray, teapot, lantern ================= */
const TRAY = { cx: 344, cy: 233, rx: 23 };
const GLASSES = [329, 339, 349, 359];
const POT = { x: 20 };
const CAT = { x: 58, y: 212 };
const LANT = { x: 446 };
const KETTLE = { x: 392 };
const KA = { base: '#a8283a', dark: '#5e1624', a: '#2c3c80', b: '#e2a646', c: '#eedcb8', edge: '#2c0e18' };
const KB = { base: '#2e3e84', dark: '#1a2252', a: '#b8303e', b: '#e8b24c', c: '#eedcb8', edge: '#10142c' };
function cushion(p, x0, y0, w, h, K, motif, sag) {
  for (let y = y0; y < y0 + h; y++) for (let i = x0; i < x0 + w; i++) {
    const lx = i - x0, top = sag ? Math.round((1 - Math.sin(Math.PI * (lx + 0.5) / w)) * sag) : 0;
    const ly = y - y0 - top, hh = h - top, ex = Math.min(lx, w - 1 - lx), ey = Math.min(ly, hh - 1 - ly);
    if (ly < 0 || ex + ey < 2) continue;
    let col;
    if (ex === 0 || ey === 0 || ex + ey === 2) col = K.edge;
    else if (ex === 1 || ey === 1) col = K.b;
    else if (ex === 2 || ey === 2) col = (((lx + ly) >> 1) & 1) ? K.dark : K.base;
    else col = motif(lx - 3, ly - 3, w - 6, hh - 6, K);
    if (ly > hh - 3 && col !== K.edge) col = mixHex(col, '#10060c', 0.35);          // shadowed underside
    else if (ly < 2 && col !== K.edge) col = mixHex(col, '#fff0d0', 0.2);          // lamp-lit top
    p.set(i, y, col);
  }
  for (const [tx, ty, dx] of [[x0 - 1, y0 + h - 3, -1], [x0 + w, y0 + h - 3, 1], [x0 - 1, y0 + 2 + (sag || 0), -1], [x0 + w, y0 + 2 + (sag || 0), 1]]) {
    p.set(tx, ty, K.b); p.set(tx + dx, ty + 1, K.b);
  }
}
const motifRow = (fx, fy, fw, fh, K) => {                // a row of small stepped lozenges
  const d = Math.abs((((fx + 3) % 8) + 8) % 8 - 3.5) + Math.abs(fy - (fh - 1) / 2);
  return d < 1 ? K.c : d < 2 ? K.a : d < 3 ? K.b : K.base;
};
const motifMedallion = (fx, fy, fw, fh, K) => {          // concentric kilim lozenge
  const d = Math.abs(fx - (fw - 1) / 2) / (fw / 2) + Math.abs(fy - (fh - 1) / 2) / (fh / 2);
  return [K.c, K.a, K.b, K.a, K.base, K.dark][Math.min(5, Math.floor(d * 5.5))];
};
const GLASS_ROWS = [                                      // tulip-shaped tea glass (5x7) on a red saucer (7x2)
  '.wwwww.',
  '.gTTTa.',
  '..gta..',
  '..gta..',
  '.gtttA.',
  '.htttA.',
  '..rrr..',
  'sSSSSSs',
  '.sssss.',
];
const GLASS_KEY = { w: '#eef2f8', g: '#c8d4e4', a: '#9aa6c0', A: '#7a86a4', T: '#ffae48', t: '#d0521c', h: '#ffe2c0', r: '#8a2a14', s: '#8e1e22', S: '#d83c3c' };
function paintRows(p, rows, key, x0, y0) {
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(x0 + i, y0 + j, col); } });
}
function renderForeground() {
  const p = painter(), r = rng(33);
  // parapet slab and wall with an Iznik tile band (lit by the lantern on the right and the tea tray glow)
  const lightAt = (i, y) => clamp(0.35 + 0.65 * Math.max(0, 1 - Math.hypot(i - LANT.x, (y - 226) * 1.4) / 110) + 0.25 * Math.max(0, 1 - Math.hypot(i - TRAY.cx, y - 232) / 90), 0, 1);
  for (let i = 0; i < W; i++) {
    const L = lightAt(i, LEDGE);
    p.set(i, LEDGE, dpick(['#5a4640', '#86685a', '#b48c6c'], L * 0.9 + r() * 0.15, i, LEDGE));
    p.set(i, LEDGE + 1, dpick(['#3e2e32', '#6a5048'], L, i, LEDGE + 1));
    p.set(i, LEDGE + 2, dpick(['#30222a', '#553e3c'], L, i, LEDGE + 2));
    p.set(i, LEDGE + 3, '#22171e'); p.set(i, LEDGE + 4, '#150e14');
  }
  const TL = { g: '#9a9eb4', b: '#2a3c86', t: '#2a8c90', r: '#a8323c', k: '#1a2350' };
  for (let y = LEDGE + 5; y < H; y++) for (let i = 0; i < W; i++) {
    const L = lightAt(i, y), ty = y - (LEDGE + 7);
    let col;
    if (ty >= 0 && ty < 10) {
      const lx = ((i % 10) + 10) % 10, ly = ty, dx = Math.abs(lx - 4.5), dy = Math.abs(ly - 4.5);
      let k = 'g';
      if (lx === 0 || ly === 0) k = 'k';
      else if (dx + dy < 1.6) k = 'r';
      else if (Math.abs(dx - dy) < 0.6 && dx + dy < 6) k = 't';
      else if (dx + dy > 6.4) k = 'b';
      col = mixHex(TL[k], '#100a14', clamp(0.9 - L * 0.95, 0.1, 0.75));
    } else col = dpick(['#140e16', '#221822', '#34242a'], L * (ty < 0 ? 1 : 0.8) - (y > 256 ? 0.2 : 0), i, y);
    p.set(i, y, col);
  }
  // tulips in a blue-and-white pot (left)
  { const x0 = POT.x, STEMS = [[-4, 204, -1], [0, 199, 0], [4, 202, 1], [8, 207, 1]];
    for (const [sx, top, lean] of STEMS) for (let y = top + 5; y < 224; y++) p.set(x0 + sx + Math.round(lean * (224 - y) / 22), y, '#3e7036');
    const leaf = (bx, dir, len, hgt) => {
      for (let k = 0; k < len; k++) {
        const y = 224 - Math.round(k * hgt / len), x = bx + Math.round(dir * k * k / (len * 1.6)), wdt = k < len * 0.7 ? 2 : 1;
        for (let j = 0; j < wdt; j++) p.set(x + j * dir, y, j === 0 ? '#5a8c52' : '#2e5a34');
      }
    };
    leaf(x0 - 3, -1, 14, 13); leaf(x0 + 5, 1, 13, 12); leaf(x0 - 1, -1, 10, 16); leaf(x0 + 2, 1, 9, 17);
    const TUL = [['#ff6a6a', '#e0283c', '#9a1428'], ['#ffe27a', '#f2b82a', '#b07a14'], ['#ffb4d0', '#f06a9e', '#b03a6a'], ['#ff7a5a', '#e0402a', '#9a2418']];
    STEMS.forEach(([sx, top, lean], k) => {
      const fx = x0 + sx + Math.round(lean * (224 - top - 5) / 22), T = TUL[k];
      ['1.1.1', '01112', '01112', '01122', '.012.'].forEach((row, j) => { for (let q = 0; q < 5; q++) if (row[q] !== '.') p.set(fx - 2 + q, top + j, T[+row[q]]); });
    });
    for (let y = 222; y < LEDGE; y++) {
      const hw = y === 222 ? 9 : y === 223 ? 7 : Math.round(8 + 1.5 * Math.sin((y - 223) / 13 * Math.PI) - (y > 233 ? (y - 233) * 0.8 : 0));
      for (let i = x0 - hw; i <= x0 + hw + 1; i++) {
        const u = (i - (x0 - hw)) / (2 * hw + 1);
        let col = dpick(['#eef2fa', '#c2cadc', '#8a92ae', '#50567a'], 0.1 + u * 0.9, i, y, 0.6);
        if (y === 222) col = '#dfe6f4';
        else if (y === 226 || y === 233) col = u < 0.6 ? '#2aa0a8' : '#1a6a74';
        else if (y > 227 && y < 232 && ((i + (y >> 1)) % 4 === 0)) col = u < 0.6 ? '#2e4aa8' : '#1c2c6a';
        p.set(i, y, col);
      }
    }
  }
  // kilim cushions: an upright one with a medallion, the cat's bed in front
  cushion(p, 34, 212, 24, 24, KA, motifMedallion, 0);
  cushion(p, 47, 224, 62, 12, KB, motifRow, 2);
  // brass tray with four tulip glasses of tea
  const brass = ['#fff0b0', '#ffd27a', '#e0a24a', '#b0742e', '#7a4a1c', '#4a2a10'];
  for (let y = LEDGE - 7; y <= LEDGE; y++) for (let i = TRAY.cx - TRAY.rx - 1; i <= TRAY.cx + TRAY.rx + 1; i++) {
    const nx = (i + 0.5 - TRAY.cx) / TRAY.rx, ny = (y + 0.5 - TRAY.cy) / 3.2, d = nx * nx + ny * ny;
    if (y === LEDGE && d <= 1.6 && Math.abs(nx) < 0.9) { p.set(i, y, brass[5]); continue; }
    if (d > 1) continue;
    let col = d > 0.62 ? (ny < 0 ? brass[1] : brass[3]) : dpick([brass[1], brass[2], brass[3]], 0.35 + nx * 0.3 + ny * 0.2, i, y, 0.6);
    if (d <= 0.62 && ((i * 3 + y * 5) % 11 === 0)) col = brass[4];
    if (d > 0.62 && ny < 0 && nx < -0.2 && nx > -0.7) col = brass[0];
    p.set(i, y, col);
  }
  for (const gx of GLASSES) paintRows(p, GLASS_ROWS, GLASS_KEY, gx - 3, 225);
  // double teapot (caydanlik): a squat kettle with a small teapot brewing on top
  const CU = ['#ffe2b8', '#f4aa70', '#c86c42', '#8a4228', '#4e2418'], K = KETTLE.x;
  const body = (top, rows) => rows.forEach((hw, j) => {
    for (let i = -hw; i <= hw; i++) {
      const u = (i + hw) / (2 * hw);
      p.set(K + i, top + j, j === rows.length - 1 ? CU[3] : u < 0.14 ? CU[2] : u < 0.34 ? CU[0] : u < 0.6 ? CU[1] : u < 0.84 ? CU[2] : CU[3]);
    }
  });
  body(225, [6, 8, 9, 9, 9, 9, 9, 9, 8, 7, 6]);
  for (let k = 0; k < 7; k++) { p.set(K + 9 + k, 228 - k, CU[0]); p.set(K + 9 + k, 229 - k, CU[1]); p.set(K + 9 + k, 230 - k, CU[3]); }
  for (const [dx, dy] of [[-10, 226], [-11, 227], [-12, 228], [-12, 229], [-12, 230], [-11, 231], [-10, 232]]) p.set(K + dx, dy, CU[3]);
  for (let i = -5; i <= 5; i++) p.set(K + i, 224, CU[4]);
  body(216, [3, 5, 5, 6, 6, 5, 4, 3]);
  for (let i = -2; i <= 2; i++) p.set(K + i, 215, i < 0 ? CU[1] : CU[3]);
  p.set(K, 214, CU[1]); p.set(K, 213, CU[0]);
  for (let k = 0; k < 5; k++) p.set(K + 6 + k, 219 - k, k < 4 ? CU[2] : CU[1]);
  for (const [dx, dy] of [[-6, 217], [-7, 218], [-7, 219], [-7, 220], [-6, 221]]) p.set(K + dx, dy, CU[3]);
  // brass lantern (the candle flickers in render)
  { const lx = LANT.x;
    for (let y = 233; y < LEDGE; y++) for (let i = lx - 5; i <= lx + 5; i++) p.set(i, y, y === 233 ? brass[1] : brass[4]);
    for (let y = 221; y < 233; y++) for (let i = lx - 4; i <= lx + 4; i++) p.set(i, y, dpick(['#ffe2a0', '#f2b058', '#b86a2a'], Math.abs(i - lx + 0.5) / 5 + (y - 221) / 30, i, y));
    for (let y = 221; y < 233; y++) { p.set(lx - 5, y, brass[2]); p.set(lx + 5, y, brass[4]); }
    for (let y = 228; y < 233; y++) { p.set(lx - 1, y, '#f6ead0'); p.set(lx, y, '#e2d2b4'); }
    [11, 11, 9, 7, 5, 3, 1].forEach((w, j) => { for (let k = -(w >> 1); k <= w >> 1; k++) p.set(lx + k, 220 - j, k < 0 ? brass[1] : brass[3]); });
    p.set(lx, 212, brass[2]); p.set(lx - 1, 211, brass[2]); p.set(lx + 1, 211, brass[2]); p.set(lx, 210, brass[2]); }
  return p.done();
}

/* ================= The sleeping tabby (two breathing poses; tail and right ear drawn per frame) ================= */
const CATC = { o: '#4a2412', d: '#a4541e', b: '#d98a3a', l: '#f2aa54', h: '#ffd08a', w: '#fbe8cc', n: '#e8888a', e: '#3a1c10' };
function renderCat(inhale) {
  const w = 31, h = 14, p = painter(w, h);
  const ry = inhale ? 6.5 : 5.9, bcy = 13.6 - ry;
  const inBody = (i, y) => ((i + 0.5 - 11.5) / 11.3) ** 2 + ((y + 0.5 - bcy) / ry) ** 2 <= 1;
  const inHead = (i, y) => ((i + 0.5 - 23.5) / 5) ** 2 + ((y + 0.5 - 9) / 4.4) ** 2 <= 1;
  const S = (i, y) => y >= 0 && y < h && (inBody(i, y) || inHead(i, y));
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    if (!S(i, y)) continue;
    const top = !S(i, y - 1), side = !S(i - 1, y) || !S(i + 1, y);
    const head = inHead(i, y) && (i > 19 || !inBody(i, y));
    let col;
    if (top) col = CATC.h;                                         // lamp-lit rim along the back and head
    else if (side) col = y > 10 ? CATC.o : CATC.d;
    else if (head) col = y < 8 ? CATC.l : CATC.b;
    else {
      const topY = bcy - ry * Math.sqrt(Math.max(0, 1 - ((i + 0.5 - 11.5) / 11.3) ** 2));
      col = y < topY + 2 ? CATC.l : CATC.b;
      if (((i + (y >> 1)) % 4) === 0 && y < 12 && y > topY + 0.8) col = CATC.d;   // tabby stripes
    }
    p.set(i, y, col);
  }
  for (const [x, y, k] of [[20, 3, 'b'], [20, 4, 'l'], [21, 4, 'n'],                   // left ear (right ear drawn per frame)
    [21, 9, 'e'], [22, 9, 'e'], [25, 9, 'e'], [26, 9, 'e'], [23, 10, 'n'], [24, 10, 'n'],   // sleeping eyes, nose
    [22, 10, 'w'], [25, 10, 'w'], [21, 11, 'w'], [22, 11, 'w'], [23, 11, 'w'], [24, 11, 'w'], [25, 11, 'w'], [26, 11, 'w'],
    [22, 12, 'w'], [23, 12, 'w'], [24, 12, 'w'], [25, 12, 'w'],
    [22, 6, 'd'], [23, 7, 'd'], [24, 7, 'd'], [25, 6, 'd']]) p.set(x, y, CATC[k]);
  return p.done();
}

/* ================= Pergola beam with a grapevine, hanging mosaic lamps ================= */
const JEWEL = {
  ruby: ['#ffe2e6', '#ff6a86', '#e02c50', '#8e1634'],
  emerald: ['#dcffe8', '#4ae89a', '#1cae66', '#0c5a38'],
  sapphire: ['#e0eaff', '#6a96ff', '#2c5ae0', '#162c86'],
  amber: ['#fff4c8', '#ffc848', '#e8901c', '#8e4c0e'],
  amethyst: ['#f4e0ff', '#c878ff', '#9040d8', '#4e1c80'],
  turquoise: ['#dcfff8', '#48e6dc', '#1aacac', '#0c5c64'],
};
const LAMPS = [
  { x: 14, len: 42, s: 2, main: 'ruby', alt: 'amber' },
  { x: 42, len: 20, s: 1, main: 'emerald', alt: 'turquoise', onion: true },
  { x: 70, len: 56, s: 2, main: 'sapphire', alt: 'amber' },
  { x: 100, len: 30, s: 1, main: 'amber', alt: 'ruby' },
  { x: 126, len: 14, s: 0, main: 'amethyst', alt: 'turquoise' },
  { x: 350, len: 16, s: 0, main: 'turquoise', alt: 'amber' },
  { x: 376, len: 36, s: 1, main: 'ruby', alt: 'emerald', onion: true },
  { x: 404, len: 22, s: 0, main: 'emerald', alt: 'amber' },
  { x: 432, len: 52, s: 2, main: 'amethyst', alt: 'turquoise' },
  { x: 462, len: 28, s: 1, main: 'amber', alt: 'sapphire' },
];
{ const r = rng(808); LAMPS.forEach((L, i) => { L.id = i; L.k = 38 + Math.floor(r() * 26); L.ph = r(); L.amp = 0.035 + r() * 0.03; L.kf = 900 + Math.floor(r() * 700); }); }
const LBRASS = ['#fff0b0', '#e8b456', '#a06a28', '#5a3814'];
function makeLamp(L) {
  const gw = [7, 9, 13][L.s], gh = [7, 10, 13][L.s] + (L.onion ? 3 : 0), w = gw, h = gh + 6;
  const p = painter(w, h), M = JEWEL[L.main], A = JEWEL[L.alt], m = w >> 1, per = L.s === 2 ? 4 : 3;
  p.set(m, 0, LBRASS[1]);
  for (let i = m - 1; i <= m + 1; i++) p.set(i, 1, i < m ? LBRASS[0] : i === m ? LBRASS[1] : LBRASS[2]);
  for (let i = 1; i < w - 1; i++) p.set(i, 2, i < m ? LBRASS[1] : LBRASS[3]);
  const cx = w / 2, cy = 3 + gh / 2;
  for (let y = 3; y < 3 + gh; y++) for (let i = 0; i < w; i++) {
    let nx = (i + 0.5 - cx) / (gw / 2);
    const ny = (y + 0.5 - cy) / (gh / 2);
    if (L.onion) nx /= Math.min(1, 0.3 + (ny + 1) * 0.75);
    const q = nx * nx + ny * ny;
    if (q > 1) continue;
    const glow = 1 - q;
    const lat = L.s === 0 ? Math.abs(y - cy + 0.5) < 1 : ((i + y) % per === 0) || ((i - y + 40) % per === 0);
    let col;
    if (q > 0.72) col = lat ? A[2] : M[3];                          // dark rim of the globe
    else if (lat) col = glow > 0.62 && ((i + y) & 1) ? A[0] : A[1];  // brass-set tesserae lines
    else if (glow > 0.86) col = M[0];                               // the bulb shining through
    else if (glow > 0.3) col = ((i * 7 + y * 3) % 5 === 0) ? M[0] : M[1];
    else col = M[2];
    p.set(i, y, col);
  }
  const by = 3 + gh;
  for (let i = m - 1; i <= m + 1; i++) p.set(i, by, i < m ? LBRASS[1] : LBRASS[2]);
  p.set(m, by + 1, LBRASS[1]); p.set(m, by + 2, LBRASS[2]);
  return { c: p.done(), w, h };
}
function renderPergola() {
  const p = painter(), r = rng(44);
  const BEAM = ['#120a07', '#20140d', '#2a1a10', '#2a1a10', '#3c2617', '#5c3c22'];
  for (let i = 0; i < W; i++) {
    for (let y = 0; y < 6; y++) p.set(i, y, BEAM[y]);
    if (r() < 0.2) p.set(i, 2 + (r() < 0.5 ? 0 : 1), '#1c120b');
  }
  for (let x = 28; x < W; x += 58) for (let y = 6; y < 9; y++) for (let k = 0; k < 4; k++) p.set(x + k, y, y === 8 ? '#4a3020' : '#22150e');
  const glowAt = (x, y) => {
    let best = null, bd = 1e9;
    for (const L of LAMPS) { const d = Math.hypot(x - L.x, (y - 8) * 0.8); if (d < bd) { bd = d; best = L; } }
    return bd < 22 ? { L: best, f: 1 - bd / 22 } : null;
  };
  const LEAF = ['.xx.', 'xxxx', 'xxxx', '.xx.'];
  const leaves = [];
  for (let k = 0; k < 230; k++) {
    const x = Math.floor(r() * (W + 8)) - 4, edge = Math.min(x, W - 1 - x);
    const reach = 6 + 26 * Math.pow(Math.max(0, 1 - edge / 85), 1.6) + r() * 5;
    leaves.push({ x, y: 2 + Math.floor(r() * reach), v: r() });
  }
  leaves.sort((a, b) => a.y - b.y);
  for (const lf of leaves) {
    const g = glowAt(lf.x + 2, lf.y + 3);
    LEAF.forEach((row, j) => { for (let q = 0; q < 4; q++) if (row[q] === 'x') {
      const i = lf.x + q, y = lf.y + j;
      let col = lf.v < 0.35 ? '#0f2418' : lf.v < 0.7 ? '#173220' : '#1f4028';
      if (j === 0 && q === 1) col = '#2a5030';
      if (g && j >= 2 && g.f > bayer(i, y) * 0.8) col = mixHex('#3a6034', JEWEL[g.L.main][1], 0.35 * g.f);
      p.set(i, y, col);
    } });
  }
  for (const [gx, gy] of [[20, 24], [58, 14], [448, 20], [410, 13], [468, 30]]) {       // grape bunches
    ['xxx', 'xxx', '.xx', '.x.'].forEach((row, j) => { for (let q = 0; q < 3; q++) if (row[q] === 'x') p.set(gx + q, gy + j, (q + j) & 1 ? '#3c2254' : '#5c3a76'); });
    p.set(gx, gy, '#8a64a8');
  }
  for (let k = 0; k < 9; k++) {                                              // curly tendrils
    let x = Math.floor(r() * W), y = 6 + Math.floor(r() * 10);
    const len = 4 + Math.floor(r() * 6);
    for (let j = 0; j < len; j++) { p.set(x + Math.round(Math.sin(j * 1.3 + k)), y + j, '#2e5a2c'); }
  }
  return p.done();
}

/* ================= Tulip petals and gulls ================= */
const PETAL_COLS = [                                    // n: the near petals' face, highlight and shaded rim
  { f: '#f0283c', b: '#a8162c', h: '#ff9098', n: ['#ff6a80', '#ffd2d8', '#d23c5a'] },
  { f: '#ff78aa', b: '#c0467a', h: '#ffc8de', n: ['#ff92bc', '#ffe2ee', '#cc568a'] },
  { f: '#ffd23c', b: '#c8941c', h: '#fff2a8', n: ['#ffda5c', '#fff6cc', '#cc9a2a'] },
];
const LANDED = [[146, 0], [171, 1], [184, 2], [232, 0], [268, 1], [301, 0], [424, 2]];   // petals lying on the ledge: x, colour
const PETAL_SHAPES = [                                  // far and mid petals; the near ones are drawn by makeNearPetal
  ['xx.', 'xxx'],
  ['.xxx.', 'xxxxx', '.xx..'],
];
function makePetal(size, pc, back) {
  const rows = PETAL_SHAPES[size], w = rows[0].length, h = rows.length, p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < w; i++) if (row[i] === 'x') {
    let col = back ? pc.b : pc.f;
    if (!back && size > 0 && y < h / 2 && i < w / 2 && (i + y) < 2) col = pc.h;
    p.set(i, y, col);
  } });
  return p.done();
}
/** a near petal on a 9x9 sprite, pixel-drawn at one of 8 turns over half a turn and 3 tilts (0 flat .. 2 edge-on), its
    face or its paler back: a teardrop with a highlight on the rim facing the lamps and a shaded rim below */
const NEAR_TURNS = 8, NEAR_TILT = [1.6, 1.05, 0.55];
function makeNearPetal(pc, turn, tilt, back) {
  const p = painter(9, 9), a = 2.9, b = NEAR_TILT[tilt], th = turn * Math.PI / NEAR_TURNS, cs = Math.cos(th), sn = Math.sin(th);
  const [f, h, sh] = back ? pc.n.map(c => mixHex(c, '#fff4f4', 0.22)) : pc.n;
  const inside = (i, y) => {
    const u = (i - 4) * cs + (y - 4) * sn, v = (y - 4) * cs - (i - 4) * sn, bw = b * (1 - 0.3 * u / a);
    return (u / a) ** 2 + (v / bw) ** 2 <= 1;
  };
  for (let y = 0; y < 9; y++) for (let i = 0; i < 9; i++) if (inside(i, y)) {
    const lit = !inside(i, y - 1) || !inside(i - 1, y - 1), shade = !inside(i, y + 1) || !inside(i + 1, y + 1);
    p.set(i, y, lit && shade ? f : lit ? h : shade ? sh : f);                          // a sliver seen edge-on shows its face
  }
  return p.done();
}
const GULL_KEY = { w: '#f4f6ff', g: '#a8b2c8', k: '#262632', y: '#f2c040', s: '#e2d6c8' };
const GULLS_ROWS = [
  [['x...x', '.x.x.', '..x..'], ['.x.x.', 'x.x.x'], ['..x..', '.x.x.', 'x...x']],
  [['k.......k', '.g.....g.', '..ww.ww..', '....w....'], ['.gw...wg.', 'k..www..k'], ['...www...', '.ww...ww.', 'k.......k']],
  [['k...................k', 'kg.................gk', '.gw...............wg.', '..gw.............wg..', '...ww...........ww...',
    '.....ww.......ww.....', '.......wwwwwwwy......', '........sssss........'],
    ['...gg...........gg...', '.ggwwg.........gwwgg.', 'kg...ww.......ww...gk', 'k......wwwwwwwy.....k', '........sssss........'],
    ['.......wwwwwwwy......', '.....ww.sssss.ww.....', '...wg...........gw...', '..gg.............gg..', '.kg...............gk.',
      'k...................k']],
];
function makeGulls() {
  return GULLS_ROWS.map((frames, size) => frames.map(rows => {
    const key = size === 0 ? { x: '#e8ecf8' } : GULL_KEY;
    return [sprite(rows, key), sprite(rows, key, true)];
  }));
}

function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.5, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 2; d[o + 1] = 4; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Rooftop billboard: steel sign on the ridge, three goose-neck floodlamps ================= */
// It stands on a roof beyond the first row of hillside houses: the ridge roofs (drawn after it) hide its feet.
const BB_FR = { x: 64, y: 114, w: 68, h: 64 };        // the frame sprite's box (scene px)
const BB_LAMPS = [BB.x + 8, BB.x + 24, BB.x + 40];    // floodlamp columns; the heads sit 7 rows above the face
const BB_TIRED = [[58.2, 59.5], [163.6, 164.5], [201.1, 201.6]];   // the middle lamp's tired bulb stutters
const STEEL = ['#6a789e', '#48567e', '#2e3a5e', '#1e2644', '#141a30'];
function renderBillboardFrame() {
  const p = painter(BB_FR.w, BB_FR.h), S = (x, y, col) => p.set(x - BB_FR.x, y - BB_FR.y, col);
  const x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h, bot = BB_FR.y + BB_FR.h;
  // lattice scaffold: three I-beam legs with X bracing and ties
  const LEGS = [x0 + 6, BB.x + (BB.w >> 1) - 1, x1 - 7];
  for (const lx of LEGS) for (let y = y1 + 1; y < bot; y++) { S(lx, y, STEEL[3]); S(lx + 1, y, STEEL[2]); }
  for (let k = 0; k + 1 < LEGS.length; k++) {
    const a = LEGS[k] + 2, b = LEGS[k + 1] - 1;
    for (const [ya, yb] of [[y1 + 3, y1 + 12], [y1 + 12, y1 + 21]]) for (let i = a; i <= b; i++) {
      const f = (i - a) / (b - a);
      S(i, Math.round(lerp(ya, yb, f)), STEEL[3]); S(i, Math.round(lerp(yb, ya, f)), STEEL[3]);
    }
    for (let i = a; i <= b; i++) { S(i, y1 + 3, STEEL[3]); S(i, y1 + 12, STEEL[3]); }
  }
  // service ladder up the middle leg
  for (let y = y1 + 2; y < bot; y++) { S(LEGS[1] + 3, y, STEEL[3]); S(LEGS[1] + 5, y, STEEL[3]); if (y % 2 === 0) S(LEGS[1] + 4, y, STEEL[2]); }
  // catwalk: lit grating, dark underside
  for (let i = x0 - 2; i <= x1 + 2; i++) { S(i, y1 + 1, (i & 1) ? '#6a6278' : '#524c64'); S(i, y1 + 2, '#161a2e'); }
  for (const lx of LEGS) { S(lx - 1, y1 + 3, STEEL[3]); S(lx + 2, y1 + 3, STEEL[3]); }   // brackets under the catwalk
  // the frame: lit top rail, a thin side showing the sign's depth on the shadowed right
  for (let i = x0; i <= x1; i++) { S(i, y0, STEEL[1]); S(i, y1, STEEL[3]); S(i, y0 - 1, STEEL[3]); }
  for (let y = y0; y <= y1; y++) { S(x0, y, STEEL[2]); S(x1, y, STEEL[3]); S(x1 + 1, y, STEEL[4]); }
  // goose-neck floodlamps: rod up from the rail, hooded head leaning out over the face
  for (const lx of BB_LAMPS) {
    for (let y = y0 - 5; y < y0 - 1; y++) { S(lx, y, STEEL[1]); S(lx + 1, y, STEEL[3]); }
    [STEEL[3], STEEL[1], STEEL[0], STEEL[1], STEEL[3]].forEach((col, k) => S(lx - 2 + k, y0 - 7, col));
    S(lx - 2, y0 - 6, STEEL[4]); S(lx + 2, y0 - 6, STEEL[4]);                // hood lip (the lens is lit per frame)
  }
  return p.done();
}
// the city's own ad: a tulip glass of tea on a gold saucer and a big ÇAY, a floodlit crimson board
const AD_BG = ['#e0685c', '#c63e46', '#a82c3a', '#8c2032', '#701828'];
const TEA_ROWS = [                                    // tulip glass, a spoon leaning in it, on a gold saucer
  '..........e..',
  '.........e...',
  '..wwwwwwwWw..',
  '..gppppppka..',
  '...gTTTTkA...',
  '....gttuA....',
  '....gttuA....',
  '...gttttuA...',
  '...httttuA...',
  '....hrrrA....',
  '.sSSSSSSSSSs.',
  '...sssssss...',
];
const TEA_KEY = { w: '#fff8ee', W: '#ffffff', e: '#eef0f8', k: '#f0c8a0', p: '#d27a7c', g: '#ffe6da', a: '#e0b0b0',
  A: '#c89096', h: '#fff2e6', T: '#ffd07a', t: '#f59a3a', u: '#d8702a', r: '#c05a22', S: '#ffd27a', s: '#a8742a' };
const TEA = { x: 2, y: 6 };                           // glass position on the face
const AD_SUBS = ['TAZE', 'SICAK', ''];               // fresh / hot, and a blank beat between them
/** FONT3 text straight into a painter, at scale k, colour picked per glyph row */
function ptext(p, s, x, y, colAt, k = 1) {
  let cx = x;
  for (const ch of s) {
    const g = FONT3[ch];
    if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1')
      for (let a = 0; a < k; a++) for (let b = 0; b < k; b++) p.set(cx + i * k + a, y + j * k + b, colAt(j)); });
    cx += 4 * k;
  }
}
function renderAd(sub) {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) {
    let lit = 0;
    for (const lx of BB_LAMPS) lit = Math.max(lit, 1 - Math.hypot((i - (lx - BB.x)) / 11, y / 7));
    const v = 0.34 + 0.5 * y / (BB.h - 1) + 0.12 * Math.abs(i - (BB.w - 1) / 2) / (BB.w / 2) - 0.5 * lit;
    p.set(i, y, dpick(AD_BG, v, i, y));
  }
  paintRows(p, TEA_ROWS, TEA_KEY, TEA.x, TEA.y);
  const tx = 21, ty = 1;                              // one row up leaves the Ç's cedilla clear of the sub line
  ptext(p, 'ÇAY', tx + 1, ty + 1, () => '#4a0c18', 2);                         // drop shadow
  ptext(p, 'ÇAY', tx, ty, j => j < 3 ? '#fff4dc' : '#ffd88a', 2);
  if (sub) ptext(p, sub, tx + 11 - (sub.length * 4 - 1) / 2 | 0, 14, () => '#ffd466');
  return p.done();
}
/** the middle lamp's light pool on the face (dithered alpha), taken away while its bulb stutters */
function renderPoolMask() {
  const [c, x] = makeCanvas(BB.w, BB.h), img = x.createImageData(BB.w, BB.h), d = img.data, cx = BB_LAMPS[1] - BB.x;
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) {
    const v = clamp(1.25 - Math.hypot((i - cx) / 15, (y + 3) / 24), 0, 1), q = Math.floor(v * 3 + bayer(i, y)) / 3;
    if (q <= 0) continue;
    const o = (y * BB.w + i) * 4; d[o] = 18; d[o + 1] = 6; d[o + 2] = 14; d[o + 3] = Math.round(Math.min(1, q) * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
function drawOwnAd(t) {
  const q = t * 30 / LOOP + 0.25, beat = frac(q) < 0.04;                         // the line changes every 8 s
  ctx.drawImage(AD_C[beat ? 2 : Math.floor(q) & 1], BB.x, BB.y);
  ctx.fillStyle = '#fff6ea';                                                   // steam curling off the glass
  for (let k = 0; k < 4; k++) {
    const u = frac(t * (38 + k * 7) / LOOP + k * 0.29), y = BB.y + TEA.y + 1 - Math.floor(u * 7);
    const x = BB.x + TEA.x + 5 + (k & 1) * 2 - (k >> 1) + Math.round(Math.sin(u * 5 + k * 2 + TAU * 9 * t / LOOP) * 1.3);
    ctx.globalAlpha = 0.85 * Math.sin(Math.PI * u); ctx.fillRect(x, y, 1, 1);
    if (u > 0.3) { ctx.globalAlpha *= 0.5; ctx.fillRect(x, y + 1, 1, 1); }
  }
  const g = t % 12;                                                            // a glint slides down the glass
  if (g < 0.7) {
    const j = 2 + Math.floor(g / 0.7 * 8), i = TEA_ROWS[j].search(/[^.]/) + 1;
    ctx.globalAlpha = 0.85; ctx.fillRect(BB.x + TEA.x + i, BB.y + TEA.y + j, 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch) {
  halo(HL.bbSky, BB.x + (BB.w >> 1), BB.y + 4, 0.9);                            // the floodlit air around the sign
  ctx.drawImage(BB_FR_C, BB_FR.x, BB_FR.y);
  if (!PITCH.covers(t, pitch)) drawOwnAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const tired = inEvent(t, BB_TIRED) ? (hash(11, Math.floor(t * 24)) < 0.55 ? 0.15 : 0.7) : 1;
  if (tired < 1) { ctx.globalAlpha = (1 - tired) * 0.45; ctx.drawImage(BB_POOL_C, BB.x, BB.y); ctx.globalAlpha = 1; }
  BB_LAMPS.forEach((lx, k) => {
    const I = (k === 1 ? tired : 1) * (0.92 + 0.08 * wave(t, 1100 + k * 131, k * 0.3));
    ctx.fillStyle = I > 0.5 ? '#fff6dc' : '#8a7a70'; ctx.fillRect(lx - 1, BB.y - 7, 3, 1);
    ctx.fillStyle = I > 0.5 ? '#ffffff' : '#b0a090'; ctx.fillRect(lx, BB.y - 7, 1, 1);
    halo(HL.bbLamp, lx, BB.y - 6, I);
  });
}

/* ================= Timings (loop seconds) and particle tables ================= */
const FERRIES = [
  { big: true, a: 14, b: 94, x0: -70, x1: 490, wl: 226, dir: 1 },       // near lane, left to right
  { big: false, a: 124, b: 204, x0: 486, x1: -52, wl: 206, dir: -1 },   // far lane, right to left
];
const HORNS = [15.6, 125.6];                          // as each ferry's bow comes into view
const NEAR_GULLS = [
  { a: 47, b: 55, dir: 1, y0: 40, dip: 22 },
  { a: 145, b: 152, dir: -1, y0: 52, dip: 18 },
  { a: 205, b: 212, dir: 1, y0: 34, dip: 26 },
];
const GULL_T = NEAR_GULLS.map(g => +((g.a + g.b) / 2).toFixed(1));
const EAR_FLICKS = [23.2, 67.5, 118.1, 161.4, 199.8, 231.0];
const TAIL_FLICKS = [38, 96, 142, 188, 225];
const LAMP_FLICK = [[72, 73.6], [176, 177.2]];
// the couple on the parapet (drawCouple): his sips, her head on his shoulder, the ferries they turn to
const COUPLE = { her: 197, him: 207 };
const SIP = { per: 24, len: 2.6, ph: 0.3 };
const LEANS = [[34, 96], [148, 206]];
const POINT_AT = [HORNS[0] + 0.8, HORNS[0] + 4.4];  // she points out the near ferry as it comes in
const LOOK_AT = [HORNS[1] + 0.6, HORNS[1] + 5.2];   // both turn to the far one's horn
const FAR_GULLS = [], MID_GULLS = [];
{
  const r = rng(515);
  for (let i = 0; i < 6; i++) FAR_GULLS.push({ cx: 70 + r() * 340, cy: 70 + r() * 60, rx: 16 + r() * 40, ry: 4 + r() * 9,
    k: (2 + Math.floor(r() * 4)) * (r() < 0.5 ? 1 : -1), ph: r(), kf: 20 + Math.floor(r() * 30), pf: r() });
  for (let i = 0; i < 3; i++) MID_GULLS.push({ cx: 120 + i * 120 + r() * 40, cy: 150 + r() * 25, rx: 40 + r() * 40, ry: 8 + r() * 8,
    k: 2 + Math.floor(r() * 3), ph: r(), kf: 25 + Math.floor(r() * 20), pf: r() });
}
const PETALS = [[], [], []];
{
  const r = rng(4242);
  [[22, 7, 10, 2, 40], [16, 10, 13, 4, 70], [8, 13, 16, 8, 110]].forEach(([count, nMin, nMax, amp, drift], size) => {
    for (let i = 0; i < count; i++) PETALS[size].push({
      id: size * 100 + i, size, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 50 + Math.floor(r() * 40), p2: r(), kr: 20 + Math.floor(r() * 30), p3: r(),
      kf: 30 + Math.floor(r() * 40), p4: r(), amp: amp * (0.7 + r() * 0.6), drift: drift * (0.6 + r() * 0.8), c: Math.floor(r() * 3),
    });
  });
}
const STEAM = [];
{
  const r = rng(61);
  GLASSES.forEach((gx, gi) => { for (let k = 0; k < 3; k++) STEAM.push({ x: gx, y: 224, n: 40 + Math.floor(r() * 12), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), id: gi * 10 + k, h: 15 }); });
  for (let k = 0; k < 4; k++) STEAM.push({ x: KETTLE.x + 15, y: 221, n: 44 + Math.floor(r() * 10), ph: r(), k: 18 + Math.floor(r() * 20), p: r(), id: 90 + k, h: 20, spout: true });
}
const CARS = [];
{
  const r = rng(71);
  for (let i = 0; i < 12; i++) CARS.push({ road: 0, dir: r() < 0.5 ? 1 : -1, n: 3 + Math.floor(r() * 4), ph: r() });
  for (let i = 0; i < 10; i++) CARS.push({ road: 1, dir: r() < 0.5 ? 1 : -1, n: 6 + Math.floor(r() * 6), ph: r() });
}
const BOATS = [{ x: 146, wl: 199, k: 37, ph: 0.1 }, { x: 428, wl: 213, k: 29, ph: 0.6 }];
const LED_BASE = ['#ff3a5c', '#ff8a2a', '#ffe04a', '#4aff8a', '#3ad0ff', '#5a6aff', '#d04aff'];
const LED_RAMP = [];
for (let i = 0; i < 70; i++) { const f = i / 70 * LED_BASE.length, k = Math.floor(f); LED_RAMP.push(mixHex(LED_BASE[k], LED_BASE[(k + 1) % LED_BASE.length], f - k)); }
const ledIdx = (s, t) => Math.floor(frac(3 * t / LOOP - s) * LED_RAMP.length) % LED_RAMP.length;
const STREAKS = [
  { x: BM.cx, w: 9, col: '#ffd27a', a: 0.42, seed: 1 }, { x: HS.cx, w: 11, col: '#ffac66', a: 0.42, seed: 2 },
  { x: GT.cx, w: 4, col: '#ffc070', a: 0.36, seed: 3 }, { x: BM.cx - 40, w: 1, col: '#ffe0a0', a: 0.25, seed: 5 },
  { x: BM.cx + 40, w: 1, col: '#ffe0a0', a: 0.25, seed: 6 }, { x: 44, w: 3, col: '#e0b070', a: 0.25, seed: 7 },
];
// a picked weather (site/js/engine/weather.js): Istanbul's own tulip petals, and the ones lying on the ledge, show only with
// its own weather. Far drops end on the hillsides and catch the floodlit mosques and Galata (and the sign's lamps), mid
// ones land on the Bosphorus around Maiden's Tower, near ones fall past the parapet through the mosaic lamps' light.
// One cfg per depth (far, mid, near): the lamps hanging over us don't light rain falling a mile away
const WEATHER = weatherPlans(['petals']);
const WX_LAND = [[174, 189], [WL + 2, LEDGE - 1], [H + 4, H + 4]];
const WX_FLOOD = [
  { x: BM.cx, y: 122, rx: 44, ry: 34, col: '#ffd890' }, { x: HS.cx, y: 124, rx: 42, ry: 32, col: '#ffb070' },
  { x: GT.cx, y: 112, rx: 12, ry: 40, col: '#ffc070' },
];
const WX_WATER = [
  { x: MT.x + MT.c, y: MT.y + 30, rx: 34, ry: 30, col: '#ffe8c0' }, ...BOATS.map(b => ({ x: b.x + 2, y: b.wl - 7, rx: 6, ry: 6, col: '#ffc060' })),
  { x: LANT.x, y: 228, rx: 24, ry: 12, col: '#ffa850' },
];
const WX_TERRACE = [                                  // each lamp's globe where drawTop hangs it at rest, a pale tint of its glass
  ...LAMPS.map(L => ({ x: L.x, y: 9 + L.len + (([7, 10, 13][L.s] + (L.onion ? 3 : 0)) >> 1), rx: [14, 18, 22][L.s], ry: [16, 21, 26][L.s],
    col: mixHex(JEWEL[L.main][0], JEWEL[L.main][1], 0.45) })),
  { x: LANT.x, y: 226, rx: 24, ry: 18, col: '#ffa850' }, { x: TRAY.cx, y: 227, rx: 28, ry: 10, col: '#ffae58' },
];
const wxDepths = far => [far, WX_WATER, WX_TERRACE].map(lights => ({ land: WX_LAND, lights }));
const WX = wxDepths(WX_FLOOD);
const WX_BB = wxDepths([...WX_FLOOD, { x: BB.x + (BB.w >> 1), y: BB.y + 2, rx: 32, ry: 16, col: '#ffc890' }]);   // + the sign's floodlamps

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR, BRIDGE, SKYLINE, MAIDEN_C, FERRY_SPR, WATER_C, FG_C, CAT_SPR, PERGOLA_C, LAMP_SPR, PETAL_SPR, NEAR_SPR, GULL_SPR, VIG_C;
let BB_FR_C, BB_POOL_C, AD_C, PITCH;
function prerender() {
  HL = {
    moon: makeHalo(34, '#9fb2ff', 0.2, 1.8), moonCore: makeHalo(14, '#e4e8ff', 0.26, 1.5), venus: makeHalo(7, '#e0e8ff', 0.45, 1.4),
    bm: makeHalo(64, '#ffb858', 0.16, 1.6), hs: makeHalo(62, '#ff9a54', 0.16, 1.6), gt: makeHalo(34, '#ffb060', 0.18, 1.6),
    gtTop: makeHalo(12, '#ffe0a0', 0.4), domeGlow: makeHalo(24, '#ffe2a0', 0.1, 1.4), balc: makeHalo(3, '#fff0c0', 0.55, 1), red: makeHalo(4, '#ff3030', 0.6, 1),
    shore: makeHalo(6, '#ffb45a', 0.3), ferry: makeHalo(28, '#ffd8a0', 0.16), maiden: makeHalo(32, '#ffe8c0', 0.2),
    beacon: makeHalo(9, '#fff6d8', 0.7, 1.2), boat: makeHalo(8, '#ffc060', 0.4),
    tray: makeHalo(46, '#ffae58', 0.12), nook: makeHalo(44, '#ffb060', 0.1), lantern: makeHalo(40, '#ffa850', 0.2), candle: makeHalo(12, '#ffe0a0', 0.5, 1.4),
    led: LED_BASE.map(c => makeHalo(5, c, 0.4, 1.2)),
    lamp: LAMPS.map(L => makeHalo([16, 20, 26][L.s], JEWEL[L.main][1], 0.34, 1.7)),
    lampCore: LAMPS.map(L => makeHalo([6, 7, 9][L.s], JEWEL[L.main][1], 0.32, 1.3)),
    bbLamp: makeHalo(6, '#ffcc40', 0.6, 1.2), bbSky: makeHalo(40, '#ffc890', 0.1, 1.5),
  };
  BB_FR_C = renderBillboardFrame(); BB_POOL_C = renderPoolMask(); AD_C = AD_SUBS.map(renderAd);
  PITCH = makePitch(BB.w, BB.h, { accent: '#e0a526' });
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR = renderFar(); BRIDGE = renderBridge(); SKYLINE = renderSkyline(); MAIDEN_C = renderMaiden();
  FERRY_SPR = [true, false].map(big => { const c = makeFerry(big); return [c, flipCanvas(c)]; });
  WATER_C = renderWater(); FG_C = renderForeground(); CAT_SPR = [renderCat(false), renderCat(true)];
  PERGOLA_C = renderPergola(); LAMP_SPR = LAMPS.map(makeLamp);
  PETAL_SPR = PETAL_COLS.map(pc => [0, 1].map(s => [makePetal(s, pc, false), makePetal(s, pc, true)]));
  NEAR_SPR = PETAL_COLS.map(pc => [false, true].map(back => [0, 1, 2].map(tilt =>
    Array.from({ length: NEAR_TURNS }, (_, turn) => makeNearPetal(pc, turn, tilt, back)))));
  GULL_SPR = makeGulls(); VIG_C = renderVignette();
}

/* ================= Per-frame drawing ================= */
function drawStars(t) {
  ctx.fillStyle = '#dfe6ff';
  for (const s of STARS) {
    const a = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha = a * 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
}
function drawMoon(t) {
  halo(HL.moon, MOON.x, MOON.y, 0.9); halo(HL.moonCore, MOON.x - 2, MOON.y + 1, 0.8);
  ctx.drawImage(MOON_C, MOON.x - 10, MOON.y - 10);
  const tw = 0.8 + 0.2 * wave(t, 37, 0.2);
  halo(HL.venus, VENUS.x, VENUS.y, tw);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(VENUS.x, VENUS.y, 1, 1);
  ctx.fillStyle = '#e4ecff'; ctx.globalAlpha = 0.85 * tw;
  ctx.fillRect(VENUS.x - 1, VENUS.y, 3, 1); ctx.fillRect(VENUS.x, VENUS.y - 1, 1, 3);
  ctx.globalAlpha = 0.3 * tw; ctx.fillRect(VENUS.x - 2, VENUS.y, 5, 1); ctx.fillRect(VENUS.x, VENUS.y - 2, 1, 5);
  ctx.globalAlpha = 1;
}
function drawClouds(t) {
  for (const L of CLOUDS) { const off = Math.floor(frac(L.k * t / LOOP) * W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); }
}
function drawPetal(b, t) {
  const q = t * b.n / LOOP + b.ph, cyc = Math.floor(q) % b.n, u = q - Math.floor(q);
  const near = b.size === 2, sh = PETAL_SHAPES[b.size], w = near ? 9 : sh[0].length, h = near ? 4 : sh.length, sw = wave(t, b.ks, b.p2);
  const y = -h * 2 - 8 + u * (H + h * 4 + 16) - Math.abs(sw) * (b.size + 1) * 1.5;
  const x = -40 - b.drift * 0.5 + hash(b.id, cyc, 5) * (W + 60) + u * b.drift + sw * b.amp;
  const rot = sw * 0.7 + 0.35 * wave(t, b.kr, b.p3);
  if (near) {                                         // tumbling: it turns, tips edge-on and flips over a few times a fall
    const fl = Math.cos(TAU * (2 * b.kf * t / LOOP + b.p4)), af = Math.abs(fl);
    const turn = ((Math.round(rot / Math.PI * NEAR_TURNS) % NEAR_TURNS) + NEAR_TURNS) % NEAR_TURNS;
    ctx.drawImage(NEAR_SPR[(b.c + cyc) % 3][fl < 0 ? 1 : 0][af > 0.66 ? 0 : af > 0.3 ? 1 : 2][turn], Math.round(x) - (w >> 1), Math.round(y) - (w >> 1));
    return;
  }
  const fl = Math.cos(TAU * (b.kf * t / LOOP + b.p4));
  ctx.save();
  ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(rot); ctx.scale(1, Math.max(0.25, Math.abs(fl)));
  ctx.drawImage(PETAL_SPR[(b.c + cyc) % 3][b.size][fl < 0 ? 1 : 0], -(w >> 1), -(h >> 1));
  ctx.restore();
}
function drawFarLights(t) {
  const s = step(t, 480);
  for (const l of FAR.lights) {
    let a = l.fixed ? 0.9 : 0.35 + 0.65 * (0.5 + 0.5 * wave(t, l.n, l.ph));
    if (!l.fixed && hash(l.id, s, 9) < 0.06) a *= 0.2;
    ctx.globalAlpha = a; ctx.fillStyle = l.col; ctx.fillRect(l.x, l.y, 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawBridgeFx(t) {
  const leds = BRIDGE.leds;
  for (let k = 0; k < leds.length; k++) {
    const L = leds[k], ci = ledIdx(L.s, t);
    ctx.fillStyle = LED_RAMP[ci]; ctx.fillRect(L.x, L.y, 1, 1);
    if (k % 4 === 0) halo(HL.led[Math.floor(ci / LED_RAMP.length * LED_BASE.length + 0.5) % LED_BASE.length], L.x, L.y, 0.8);
  }
  if (frac(t / 1.5) < 0.5) { ctx.fillStyle = '#ff3030'; ctx.fillRect(BR.tx - 3, BR.top - 2, 1, 1); ctx.fillRect(BR.tx + 3, BR.top - 2, 1, 1); halo(HL.red, BR.tx, BR.top - 2, 0.8); }
  for (const c of CARS) if (c.road === 1) {
    const u = frac(t * c.n / LOOP + c.ph), x = Math.round(c.dir > 0 ? 386 + u * 100 : 484 - u * 100);
    if (x < 388 || x >= W) continue;
    ctx.fillStyle = c.dir > 0 ? '#ff5a4a' : '#fff4d0'; ctx.fillRect(x, BR.deck - 1, 1, 1);
  }
}
function drawCityFx(t) {
  for (const w of SKYLINE.wins)
    if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) { ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, 1, 1); }
  ctx.fillStyle = '#fff6d0';
  for (const b of SKYLINE.bl) {
    const on = 0.7 + 0.3 * wave(t, 40 + (b.id % 7) * 11, b.id * 0.13);
    ctx.globalAlpha = on; ctx.fillRect(b.x, b.y, 1, 1); ctx.fillRect(b.x + 4, b.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  for (const b of SKYLINE.bl) halo(HL.balc, b.x + 2, b.y, 0.55);
  halo(HL.gtTop, GT.cx, 101, 0.6 + 0.15 * wave(t, 12));
  halo(HL.domeGlow, BM.cx, 118, 0.9); halo(HL.domeGlow, HS.cx, 120, 0.9);
  if (frac(t / 2) < 0.45) { ctx.fillStyle = '#ff4040'; ctx.fillRect(GT.cx, 78, 1, 1); halo(HL.red, GT.cx, 78, 1); }
  SKYLINE.lamps.forEach((l, i) => halo(HL.shore, l.x, l.y, 0.8 + 0.2 * wave(t, 600 + i * 31)));
  for (const c of CARS) if (c.road === 0) {
    const u = frac(t * c.n / LOOP + c.ph), x = Math.round(c.dir > 0 ? -4 + u * 348 : 344 - u * 348);
    if (x < 0 || x > 340) continue;
    ctx.fillStyle = c.dir > 0 ? '#ff5a4a' : '#fff4d0'; ctx.fillRect(x, 185, 2, 1);
  }
}
function gullFrame(t, g) { return wave(t, g.kf, g.pf) > 0.5 ? [0, 1, 2, 1][step(t, 1200) % 4] : 1; }
function drawFarGulls(t) {
  for (const g of FAR_GULLS) {
    const a = TAU * (g.k * t / LOOP + g.ph), x = g.cx + g.rx * Math.cos(a), y = g.cy + g.ry * Math.sin(a);
    ctx.drawImage(GULL_SPR[0][gullFrame(t, g)][0], Math.round(x) - 2, Math.round(y) - 1);
  }
}
function drawMidGulls(t) {
  for (const g of MID_GULLS) {
    const a = TAU * (g.k * t / LOOP + g.ph), x = g.cx + g.rx * Math.sin(a), y = g.cy + g.ry * Math.sin(2 * a);
    ctx.drawImage(GULL_SPR[1][gullFrame(t, g)][0], Math.round(x) - 4, Math.round(y) - 2);
  }
}
function drawNearGull(t) {
  for (const g of NEAR_GULLS) {
    if (t < g.a || t >= g.b) continue;
    const s = (t - g.a) / (g.b - g.a), x = g.dir > 0 ? lerp(-24, W + 8, s) : lerp(W + 8, -24, s);
    const y = g.y0 + g.dip * Math.sin(Math.PI * s);
    const fr = (s < 0.3 || (s > 0.6 && s < 0.8)) ? [0, 1, 2, 1][step(t, 1680) % 4] : 1;
    ctx.drawImage(GULL_SPR[2][fr][g.dir > 0 ? 0 : 1], Math.round(x), Math.round(y));
  }
}

function drawWater(t) {
  ctx.drawImage(WATER_C, 0, WL);
  const rows = LEDGE - WL, src = ctx.canvas;                // rippled, shimmering mirror of the city
  for (let k = 0; k < rows; k++) {
    const sy = WL - 1 - Math.floor(k * 1.5);
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + k * 0.55) * (0.6 + k * 0.05) + Math.sin(TAU * 96 * t / LOOP - k * 1.3) * 0.7);
    ctx.globalAlpha = 0.62 * (1 - k / (rows + 24)) * (0.8 + 0.2 * Math.sin(k * 1.3 - TAU * 64 * t / LOOP));
    ctx.drawImage(src, 0, sy, W, 1, off, WL + k, W, 1);
  }
  ctx.globalAlpha = 1;
  rect(ctx, 0, WL, W, rows, 'rgba(6,14,44,0.16)');
  rect(ctx, 0, WL, W, 1, '#0a0e26');
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, LEDGE);
  SKYLINE.lamps.forEach((l, i) => { if (i % 2 === 0) streak(ctx, t, l.x, 2, '#ffb454', 0.3, 20 + i, WL + 1, LEDGE); });
  for (const bx of [452, 470]) streak(ctx, t, bx, 2, LED_RAMP[ledIdx((bx - BR.tx) / 90, t)], 0.3, bx, WL + 1, LEDGE);
  const gs = step(t, 1440);
  for (let k = 0; k < 44; k++) {
    if (hash(k, gs, 17) < 0.35) continue;
    const x = Math.floor(hash(k, gs, 18) * W), y = WL + 2 + Math.floor(hash(k, gs, 19) * (LEDGE - WL - 4));
    const warm = STREAKS.some(s => Math.abs(s.x - x) < s.w + 4);
    ctx.globalAlpha = warm ? 0.8 : 0.45; ctx.fillStyle = warm ? '#ffe2a0' : '#8ea4e0';
    ctx.fillRect(x, y, warm ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawWake(f, x, w, t) {
  const big = f.big, stern = f.dir > 0 ? x + 3 : x + w - 4, bow = f.dir > 0 ? x + w - 2 : x + 1;
  const len = big ? 80 : 52, s = step(t, 1200);
  ctx.fillStyle = '#eef4ff';
  for (let d = 1; d < len; d++) {
    const xx = stern - f.dir * d;
    if (xx < 0 || xx >= W) continue;
    const fade = 1 - d / len;
    const yn = f.wl + 1 + Math.round(d * (big ? 0.12 : 0.08));
    if (yn < LEDGE && hash(d, s, 41) < 0.75) { ctx.globalAlpha = 0.75 * fade; ctx.fillRect(xx, yn, 1, 1); }
    if (hash(d, s, 42) < 0.6) { ctx.globalAlpha = 0.45 * fade; ctx.fillRect(xx, f.wl - Math.round(d * (big ? 0.05 : 0.035)), 1, 1); }
    if (d < len * 0.6 && hash(d, s, 43) < 0.9 * fade) { ctx.globalAlpha = 0.9 * fade; ctx.fillRect(xx, f.wl + (hash(d, s, 44) < 0.5 ? 0 : 1), 1, 1); }
  }
  ctx.globalAlpha = 0.9;
  for (let k = 0; k < (big ? 6 : 4); k++) if (hash(k, s, 45) < 0.8) ctx.fillRect(bow + f.dir * (k >> 1), f.wl - (k & 1), 1, 1);
  ctx.globalAlpha = 1;
}
function drawFerry(f, t) {
  if (t < f.a || t >= f.b) return;
  const spr = FERRY_SPR[f.big ? 0 : 1][f.dir > 0 ? 0 : 1], w = spr.width, h = spr.height;
  const x = Math.round(lerp(f.x0, f.x1, (t - f.a) / (f.b - f.a))), y = f.wl - h + 1;
  const rows = Math.min(f.big ? 10 : 8, LEDGE - f.wl - 1);
  for (let j = 0; j < rows; j++) {
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = 0.34 * (1 - j / rows);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, f.wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  drawWake(f, x, w, t);
  ctx.drawImage(spr, x, y);
  const cx = x + (w >> 1);
  for (const o of f.big ? [-16, 0, 16] : [-10, 6]) streak(ctx, t, cx + o, 2, '#ffd98a', 0.3, o + 7, f.wl + 1, LEDGE);
  halo(HL.ferry, cx, y + (h >> 1), 0.6);
}
function drawMaiden(t) {
  const { x, y, w, c, wl: r } = MT, wl = y + r, rows = LEDGE - wl - 1;
  for (let j = 0; j < rows; j++) {                   // its own rippled reflection, down to the parapet
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + j * 0.6) * (0.4 + j * 0.12));
    ctx.globalAlpha = 0.38 * (1 - j / (rows + 8));
    ctx.drawImage(MAIDEN_C, 0, r - 1 - j, w, 1, x + off, wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  streak(ctx, t, x + c, 8, '#ffe0a0', 0.32, 9, wl + 2, LEDGE);
  halo(HL.maiden, x + c, y + 26, 0.9);
  ctx.drawImage(MAIDEN_C, x, y);
  const s = step(t, 960);
  ctx.fillStyle = '#eef4ff';
  for (let k = 0; k < 14; k++) if (hash(k, s, 51) < 0.5) { ctx.globalAlpha = 0.7; ctx.fillRect(x + Math.floor(hash(k, s, 52) * w), wl + (k & 1), 1, 1); }
  ctx.globalAlpha = 1;
  const bt = frac(t / 4);                            // the lighthouse flash in the lantern
  if (bt < 0.14) { ctx.fillStyle = '#ffffff'; ctx.fillRect(x + c - 1, y + 12, 3, 1); halo(HL.beacon, x + c, y + 12, 0.75 - bt / 0.14 * 0.35); }
}
function drawBoat(b, t) {
  {
    const y = b.wl + Math.round(wave(t, b.k, b.ph) * 0.8), fl = 0.8 + 0.2 * wave(t, 1300 + b.k, b.ph);
    streak(ctx, t, b.x + 2, 2, '#ffc060', 0.35 * fl, b.k, y + 1, LEDGE);
    ctx.fillStyle = '#3a3048'; ctx.fillRect(b.x - 4, y - 2, 9, 1);
    ctx.fillStyle = '#1a1626'; ctx.fillRect(b.x - 4, y - 1, 9, 1); ctx.fillRect(b.x - 3, y, 7, 1);
    ctx.fillStyle = '#141020'; ctx.fillRect(b.x - 2, y - 5, 2, 3); ctx.fillRect(b.x - 2, y - 6, 1, 1);
    ctx.fillStyle = '#2a2436'; ctx.fillRect(b.x + 2, y - 6, 1, 4);
    ctx.fillStyle = '#ffd070'; ctx.fillRect(b.x + 2, y - 7, 1, 1);
    halo(HL.boat, b.x + 2, y - 7, fl);
  }
}
function drawCat(t) {
  const X = CAT.x, Y = CAT.y;
  ctx.drawImage(CAT_SPR[wave(t, 64, 0.1) > 0.15 ? 1 : 0], X, Y);
  const flick = EAR_FLICKS.some(e => t >= e && t < e + 0.35);
  ctx.fillStyle = CATC.b; ctx.fillRect(X + (flick ? 28 : 27), Y + (flick ? 4 : 3), 1, 1);
  ctx.fillStyle = CATC.l; ctx.fillRect(X + 27, Y + 4, 1, 1);
  ctx.fillStyle = CATC.n; ctx.fillRect(X + 26, Y + 4, 1, 1);
  for (let i = 1; i <= 18; i++) {
    const yy = Y + 12 - (i < 3 ? 1 : 0);
    ctx.fillStyle = i % 4 === 0 ? CATC.d : CATC.l; ctx.fillRect(X + i, yy, 1, 1);
    ctx.fillStyle = i % 4 === 0 ? CATC.o : CATC.b; ctx.fillRect(X + i, yy + 1, 1, 1);
  }
  let lift = 0.6 + 0.6 * wave(t, 23, 0.3);
  for (const f of TAIL_FLICKS) { const d = t - f; if (d >= 0 && d < 1.2) lift += 2.2 * Math.abs(Math.sin(d / 1.2 * Math.PI * 3)) * (1 - d / 1.2); }
  const L = Math.round(clamp(lift, 0, 3));
  ctx.fillStyle = CATC.l; ctx.fillRect(X + 19, Y + 12 - Math.min(1, L), 1, 1);
  ctx.fillStyle = CATC.d; ctx.fillRect(X + 20, Y + 11 - L, 1, 1 + Math.min(1, L));
  ctx.fillStyle = CATC.o; ctx.fillRect(X + 20, Y + 10 - L, 1, 1);
}
/* ================= A couple on the terrace wall =================
   They sit on the parapet with their backs to us and their legs over the far side, watching the old city across the
   water: a man in a slate jacket with a tulip glass of tea on its saucer beside him, a woman in a rose coat and a pale
   headscarf. Lit warm on the back by the terrace's lamps, a cool line of the city's glow on the hair.
   Timeline (loop seconds):
     sips     he lifts the glass to his lips for 2.6 s every 24 s; the rest of the time it steams on the coping
     15.6     the near ferry's horn: she points it out as it comes in (16.4-20)
     34-96    she rests her head on his shoulder; he leans his towards hers
     125.6    the far ferry's horn: both look round to the right (126.2-130.8)
     148-206  her head on his shoulder again
     between  she turns to him to talk for 3 s every 20 s, and he turns back */
const NIGHT = '#0a1030', LAMPLIT = '#ffd8a8', CITYLIT = '#a8bce8';
function sitterLook(top, bot, skin, hair) {
  return { top: mixHex(top, NIGHT, 0.3), topL: mixHex(top, LAMPLIT, 0.32), topD: mixHex(top, NIGHT, 0.58), bot: mixHex(bot, NIGHT, 0.35),
    skin: mixHex(skin, NIGHT, 0.3), hair: mixHex(hair, NIGHT, 0.25), hairL: mixHex(hair, CITYLIT, 0.35), hairD: mixHex(hair, NIGHT, 0.5) };
}
const HIM = sitterLook('#5a6c94', '#2e2c3a', '#c48c60', '#2a1c16');
const HER = sitterLook('#a83c54', '#2a2632', '#e2ac82', '#eccaae');     // her hair colours are the headscarf's
/** a raised sleeve, catching the light, from (sx, sy) to the hand (hx, hy) */
function sleeve(P, sx, sy, hx, hy) {
  const n = Math.max(Math.abs(hx - sx), Math.abs(hy - sy), 1);
  for (let k = 0; k < n; k++) px(ctx, Math.round(sx + (hx - sx) * k / n), Math.round(sy + (hy - sy) * k / n), P.top);
  px(ctx, hx, hy, P.skin);
}
/** one of the couple from behind, sitting on the coping, torso tw wide centred on x. o.lean / o.dip: the head px sideways
    and down; o.tilt: the shoulders follow it 1 px; o.turn: -1 / 1, a cheek shows as the head turns; o.armL: a raised
    left hand [x, y]; o.sip: the right hand holds the glass to the lips; o.scarf: a headscarf over the head and shoulders */
function sitter(x, P, tw, o = {}) {
  const lean = o.lean || 0, tx = x - (tw >> 1) + (o.tilt || 0), ty = LEDGE - 12, th = 10, hx = x - 2 + lean, hy = ty - 6 + (o.dip || 0);
  rect(ctx, x - (tw >> 1) - 1, LEDGE - 2, tw + 2, 2, P.bot);                         // seated on the coping
  rect(ctx, tx + 1, ty, tw - 2, 1, P.topL); rect(ctx, tx, ty + 1, tw, th - 1, P.top);   // round shoulders, lit across the top
  px(ctx, tx, ty + 1, P.topL); rect(ctx, tx + tw - 1, ty + 1, 1, th - 1, P.topD);
  if (o.armL) sleeve(P, tx - 1, ty + 2, o.armL[0], o.armL[1]);
  else { rect(ctx, tx - 1, ty + 2, 1, th - 2, P.topD); px(ctx, tx - 1, LEDGE - 2, P.skin); }
  if (o.sip) {                                                                        // elbow out, the glass at the lips
    rect(ctx, tx + tw, ty + 2, 1, 2, P.topD); sleeve(P, tx + tw + 1, ty + 3, hx + 5, hy + 5);
    px(ctx, hx + 6, hy + 1, '#eef2f8'); rect(ctx, hx + 6, hy + 2, 1, 2, '#f08a30'); px(ctx, hx + 6, hy + 4, '#c04a1a');
  } else { rect(ctx, tx + tw, ty + 2, 1, th - 2, P.topD); px(ctx, tx + tw, LEDGE - 2, P.skin); }
  if (o.scarf) {                                                                      // wrapped round the head, over the shoulders
    rect(ctx, hx, hy - 1, 5, 1, P.hairL); rect(ctx, hx - 1, hy, 7, 6, P.hair); rect(ctx, hx - 1, hy, 1, 5, P.hairL); rect(ctx, hx + 5, hy + 1, 1, 5, P.hairD);
    rect(ctx, tx, ty, tw, 2, P.hair); px(ctx, tx, ty, P.hairL); rect(ctx, tx + tw - 1, ty, 1, 2, P.hairD);
    if (o.turn) px(ctx, o.turn > 0 ? hx + 5 : hx - 1, hy + 3, P.skin);
  } else {
    rect(ctx, hx, hy, 5, 6, P.hair); rect(ctx, hx + 1, hy, 3, 1, P.hairL); px(ctx, hx, hy + 1, P.hairL);
    rect(ctx, hx + 1, hy + 5, 3, 1, P.skin);                                            // the nape
    rect(ctx, hx - 1, hy + 2, 1, 2, P.skin); rect(ctx, hx + 5, hy + 2, 1, 2, P.skin);   // the ears
    if (o.turn) rect(ctx, o.turn > 0 ? hx + 5 : hx - 1, hy + 3, 1, 2, P.skin);
  }
}
function drawCouple(t) {
  const { her, him } = COUPLE, lean = env(t, LEANS[0][0], LEANS[0][1], 2) + env(t, LEANS[1][0], LEANS[1][1], 2);
  const point = inEvent(t, [POINT_AT]), look = inEvent(t, [LOOK_AT]), talk = !lean && frac(t / 20 + 0.55) < 0.16;
  const sip = frac(t / SIP.per + SIP.ph) < SIP.len / SIP.per;
  const gx = him + 7;                                                                 // his glass on its saucer
  rect(ctx, gx - 1, LEDGE - 1, 4, 1, '#d83c3c');
  if (!sip) {
    px(ctx, gx, LEDGE - 4, '#eef2f8'); px(ctx, gx + 1, LEDGE - 4, '#c8d4e4');
    rect(ctx, gx, LEDGE - 3, 2, 2, '#d0521c'); px(ctx, gx, LEDGE - 3, '#ffae48');
    ctx.fillStyle = '#fffaf4';
    for (let k = 0; k < 2; k++) {
      const u = frac(t * (31 + k * 6) / LOOP + k * 0.5), sx = gx + k + Math.round(Math.sin(u * 6 + k * 2) * u * 1.4);
      ctx.globalAlpha = 0.7 * Math.min(1, u * 5) * (1 - u); ctx.fillRect(sx, LEDGE - 5 - Math.floor(u * 8), 1, 1);
    }
    ctx.globalAlpha = 1;
  }
  sitter(him, HIM, 8, { sip, lean: lean > 0.5 ? -1 : 0, turn: look ? 1 : talk && frac(t / 20 + 0.55) > 0.05 ? -1 : 0 });
  const tx = her - 3, hy = LEDGE - 18;
  sitter(her, HER, 7, { scarf: true, lean: point ? -1 : look ? 1 : Math.round(3 * lean) || (talk ? 1 : 0), dip: Math.round(2 * lean),
    tilt: lean > 0.5 ? 1 : 0, turn: point ? -1 : look || talk ? 1 : 0, armL: point ? [tx - 4, hy - 2] : null });
}
function drawSteam(t) {
  ctx.fillStyle = '#fffaf4';
  for (const s of STEAM) {
    const q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = Math.round(s.x + wave(t, s.k, s.p) * 1.8 * u + Math.sin(u * 7 + s.id) * 1.5 * u + (s.spout ? u * 6 : 0));
    const y = Math.round(s.y - u * s.h), a = 0.9 * Math.min(1, u * 6) * (1 - u) * (1 - u * 0.3);
    ctx.globalAlpha = a; ctx.fillRect(x, y, 1, 2);
    if (u > 0.25) { ctx.globalAlpha = a * 0.55; ctx.fillRect(x + (s.id & 1 ? 1 : -1), y - 1, 1, 2); }
  }
  ctx.globalAlpha = 1;
}
function drawForeground(t, petals) {
  ctx.drawImage(FG_C, 0, 0);
  if (petals) for (const [fx, k] of LANDED) {                // tulip petals that have already landed on the ledge
    const pc = PETAL_COLS[k];
    rect(ctx, fx, LEDGE - 1, 2, 1, pc.f); px(ctx, fx + 2, LEDGE - 1, pc.b); px(ctx, fx + 1, LEDGE - 2, pc.h);
  }
  halo(HL.tray, TRAY.cx, 226, 0.7); halo(HL.nook, 72, 222, 0.8);
  drawCouple(t);
  drawCat(t);
  drawSteam(t);
  const f = 0.72 + 0.14 * wave(t, 1733, 0.2) + 0.09 * wave(t, 2711, 0.6) + 0.05 * wave(t, 419);
  if (f < 0.8) { ctx.globalAlpha = (0.8 - f) * 1.5; rect(ctx, LANT.x - 4, 221, 9, 12, '#3a1a08'); ctx.globalAlpha = 1; }
  const fx = LANT.x - 1 + (wave(t, 997, 0.4) > 0.6 ? 1 : 0);
  px(ctx, fx, 227, '#fff4c0'); px(ctx, fx, 226, '#ffd060');
  if (f > 0.74) px(ctx, fx, 225, '#ff9a30');
  halo(HL.lantern, LANT.x, 226, f); halo(HL.candle, LANT.x - 1, 226, f);
}
function drawTop(t) {
  ctx.drawImage(PERGOLA_C, 0, 0);
  const flick = inEvent(t, LAMP_FLICK) ? hash(3, Math.floor(t * 20)) : 1;
  for (const L of LAMPS) {
    const th = L.amp * wave(t, L.k, L.ph), spr = LAMP_SPR[L.id], top = 6 + L.len, dx = Math.round(th * L.len);
    for (let y = 6; y < top; y++) { ctx.fillStyle = (y & 1) ? '#c89448' : '#5e4222'; ctx.fillRect(L.x + Math.round(th * (y - 6)), y, 1, 1); }
    ctx.drawImage(spr.c, L.x + dx - (spr.w >> 1), top);
    let I = 0.9 + 0.1 * wave(t, L.kf, L.ph);
    if (L.id === 6) I *= flick > 0.45 ? 1 : flick > 0.2 ? 0.5 : 0.2;
    if (I < 0.8) { ctx.globalAlpha = (0.8 - I) * 0.9; rect(ctx, L.x + dx - (spr.w >> 1), top + 3, spr.w, spr.h - 6, '#140a18'); ctx.globalAlpha = 1; }
    const cy = top + 3 + ((spr.h - 6) >> 1);
    halo(HL.lamp[L.id], L.x + dx, cy, I); halo(HL.lampCore[L.id], L.x + dx, cy, I);
  }
}

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's
// own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  ctx.drawImage(SKY_C, 0, 0);
  drawStars(t);
  drawMoon(t);
  drawClouds(t);
  if (wx.petals) for (const b of PETALS[0]) drawPetal(b, t);
  halo(HL.bm, BM.cx, 120, 0.95 + 0.05 * wave(t, 6)); halo(HL.hs, HS.cx, 122, 0.95 + 0.05 * wave(t, 6, 0.5));
  halo(HL.gt, GT.cx, 108, 0.9);
  ctx.drawImage(FAR.c, 0, 0);
  drawFarLights(t);
  ctx.drawImage(BRIDGE.c, 0, 0);
  drawBridgeFx(t);
  if (billboard) drawBillboard(t, pitch);           // behind the ridge roofs, which hide its feet
  ctx.drawImage(SKYLINE.c, 0, 0);
  drawCityFx(t);
  drawFarGulls(t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  drawWater(t);                                     // then everything on the water, far to near
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);        // behind the boats, so nothing lands on a ferry's cabin
  drawBoat(BOATS[0], t);
  drawFerry(FERRIES[1], t);
  drawBoat(BOATS[1], t);
  drawMaiden(t);
  drawFerry(FERRIES[0], t);
  if (wx.petals) for (const b of PETALS[1]) drawPetal(b, t);
  drawMidGulls(t);
  drawForeground(t, wx.petals);
  drawTop(t);
  drawNearGull(t);
  if (wx.petals) for (const b of PETALS[2]) drawPetal(b, t);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'istanbul',
  name: 'Istanbul',
  tagline: 'Bosphorus by night',
  timeZone: 'Europe/Istanbul',
  accent: '#e0a526',
  focusX: FOCUS,
  landmark: [BM.cx - 52, BM.cx + 52],                  // the Blue Mosque and its minarets
  landmarkTop: 76,                                     // the row of the minarets' finials, which landscape crops keep
  billboard: BB,
  ambience: { water: 0.4, birds: 0.35, wind: 0.2, traffic: 0.15 },
  events: [
    { t: HORNS[0], type: 'horn' }, { t: GULL_T[0], type: 'gull' }, { t: HORNS[1], type: 'horn' },
    { t: GULL_T[1], type: 'gull' }, { t: GULL_T[2], type: 'gull' },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
